import {
  CollisionDetection,
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  pointerWithin,
  rectIntersection,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import CloseRounded from '@mui/icons-material/CloseRounded';
import { Backdrop, Box, CircularProgress, Dialog, IconButton, Tooltip, Typography } from '@mui/material';
import { UseQueryResult } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useSnackbar } from 'notistack';
import { FC, KeyboardEvent, memo, MouseEvent, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import ReactGA from 'react-ga4';
import { ConfirmDialog } from 'src/components/ConfirmDialog';
import { buildPlanningEntity } from 'src/components/dispatch/autoDispatch/buildPlanningEntity';
import { formatAutoDispatchFailure } from 'src/components/dispatch/autoDispatch/formatAutoDispatchFailure';
import { resolveAutoDispatchAvailability } from 'src/components/dispatch/autoDispatch/resolveAutoDispatchAvailability';
import { AutoDispatchHooks, useAutoDispatchController } from 'src/components/dispatch/autoDispatch/useAutoDispatchController';
import { usePlanningDialogSettings } from 'src/components/dispatch/autoDispatch/usePlanningDialogSettings';
import { resolveAutoDispatchSelectionReason } from 'src/components/dispatch/autoDispatchSelection';
import { buildMoveTargets } from 'src/components/dispatch/buildMoveTargets';
import { buildNotAllocReasonsByOrderId } from 'src/components/dispatch/buildNotAllocReasonsByOrderId';
import { DetailPanelShell } from 'src/components/dispatch/detailPanel/DetailPanelShell';
import { resolveMoveSourceRowId } from 'src/components/dispatch/moveSourceRow';
import { buildMoveTargetRows } from 'src/components/dispatch/moveTargetRows';
import { OrderAttrFilter } from 'src/components/dispatch/orderAttrFilter';
import PanelResizeHandle from 'src/components/dispatch/panelResize/PanelResizeHandle';
import {
  fitPanelWidths, isPanelWidth, reservedWidthFor,
} from 'src/components/dispatch/panelResize/panelWidth';
import { parseUnassignedIds } from 'src/components/dispatch/parseUnassignedIds';
import { resolveProgressToastLabel } from 'src/components/dispatch/progressToastLabel';
import { collectResetTargets } from 'src/components/dispatch/resetTargets';
import { resolveBusyGuardReason } from 'src/components/dispatch/resolveBusyGuardReason';
import { allRowsSelected, collectSelectableRowIds, pruneSelectedRowIds } from 'src/components/dispatch/selectableRowIds';
import { allSelectedShiftsHaveNoPlan } from 'src/components/dispatch/selectedShiftTargets';
import { resolveSwapBlockReason } from 'src/components/dispatch/swapGuard';
import TariffCalculationDialog from 'src/components/dispatch/TariffCalculationDialog';
import { buildTruckColorMap } from 'src/components/dispatch/truckColorMap';
import { useDisplayMetrics } from 'src/components/dispatch/unassignedDisplay/useDisplayMetrics';
import { useApplyResetToCaches } from 'src/components/dispatch/useApplyResetToCaches';
import { useScrollToRow } from 'src/components/dispatch/useScrollToRow';
import { useTariffCalculationAction } from 'src/components/dispatch/useTariffCalculationAction';
import { useTruckGroupSelection } from 'src/components/dispatch/useTruckGroupSelection';
import { hideDeletingShifts } from 'src/components/dispatch/vehicleAssignment/hideDeletingShifts';
import { useShiftDeletion } from 'src/components/dispatch/vehicleAssignment/useShiftDeletion';
import { useVehicleAssignmentEditing } from 'src/components/dispatch/vehicleAssignment/useVehicleAssignmentEditing';
import { VehicleAssignmentMenus } from 'src/components/dispatch/vehicleAssignment/VehicleAssignmentMenus';
import { applyVehicleOverrides, useVehicleDisplayOverrides } from 'src/components/dispatch/vehicleAssignment/vehicleDisplayOverrides';
import { buildConstraintAlertsByRowId } from 'src/components/dispatch/vehicleConstraintAlerts';
import {
  ALL_ROTATION_FILTERS,
  buildTruckGroupsWithAll,
  filterVehicleRowGroups,
  RotationFilterValue,
  VehicleFilterMode,
} from 'src/components/dispatch/vehicleFilter';
import { buildPeakLoadByRowId } from 'src/components/dispatch/vehicleLoad';
import { hasDispatchableCard } from 'src/components/dispatch/vehicleRowExpand/assignedDelivery';
import { ReportPrintingDialogs, useReportPrinting } from 'src/components/ReportPrinting';
import { TRUCK_UNSET_LABEL } from 'src/constants/assignmentLabels';
import { appBarHeight } from 'src/constants/layout';
import { SCREEN_NAMES } from 'src/constants/screenNames';
import {
  ConvenienceDispatchFixedRouteDetailEntity,
  ConvenienceDispatchFixedRouteEntity,
} from 'src/entities/ConvenienceDispatchFixedRoute.entity';
import { DeliveryCard, DispatchCard, UnassignedItem, Vehicle } from 'src/entities/dispatch';
import { DeliveryListGroupingValues } from 'src/entities/licenseEntity';
import { PlanningSettingRequest } from 'src/entities/PlanningHistory.entity';
import { PlanningsDeliveryEntity, PlanningsDeliveryWithLegRunEntity } from 'src/entities/PlanningsDelivery.entity';
import { PlanningsDriverEntity } from 'src/entities/PlanningsDriver.entity';
import { PlanningsGroupEntity } from 'src/entities/PlanningsGroup.entity';
import { PlanningsTruckEntity } from 'src/entities/PlanningsTruck.entity';
import { ShipmentOrderLegRunForPlanEntity } from 'src/entities/ShipmentOrderLegRun.entity';
import { TimelineRequestEntity } from 'src/entities/TimelineRequest.entity';
import { useKeepSetIdentity } from 'src/hooks/useKeepIdentity';
import { usePersistedJSON } from 'src/hooks/usePersistedJSON';
import { useMutationPlanningV5 } from 'src/hooks/useQueryPlanning';
import { useQueryPlanningGroups } from 'src/hooks/useQueryPlanningGroups';
import { PlanningsDeliveriesQueryResult } from 'src/hooks/useQueryPlanningsDeliveries';
import { useQueryPlanningsNotAllocReasons } from 'src/hooks/useQueryPlanningsNotAllocReasons';
import { useQueryPlanningsSuggestions } from 'src/hooks/useQueryPlanningsSuggestions';
import { useMutationPlanningTruckDisplayOrders } from 'src/hooks/useQueryPlanningsTrucks';
import { ShipmentOrderLegRunsQueryResult } from 'src/hooks/useQueryShipmentOrderLegRuns';
import { useMutationShipmentOrder } from 'src/hooks/useQueryShipmentOrders';
import { useMutationShipmentOrderLegRun } from 'src/hooks/useShipmentOrderLegRun';
import { debounce } from 'src/utils/debounce';
import { compareTrucksByDisplayOrder } from 'src/utils/truckDisplayOrder.util';
import { RelaxedRuleValues, RelaxedRuleVo } from 'src/vo/RelaxedRule.vo';


import NotAllocSuggestionComponent from '../V3Plans/components/NotAllocSuggestion.component';
import PlanningPresenter from '../V3Plans/presenters/PlanningPresenter';

import {
  buildVehicleRowGroups,
  createVehicleRowGroupsCache,
  rowIdFromTruck,
  toUnassignedItem,
  VehicleInfoExtra,
  VehicleRowGroup,
  VehicleRowGroupsCache,
} from './adapter';
import { applyOverrideRowCards } from './applyOverrideRowCards';
import {
  resetOverrideForScopeChange,
  shouldClearOverrideAfterPlanningResult,
  shouldResetOverrideAfterServerRefresh,
} from './autoDispatchQueue';
import { BoardInteractionProvider } from './BoardInteractionContext';
import { BoardInteractionStore, createBoardInteractionStore } from './boardInteractionStore';
import { buildGrossProfitByTruckId } from './buildGrossProfitByTruckId';
import { buildShiftTimeByTruckId } from './buildShiftTimeByTruckId';
import { buildTimelineRequestEntities, ComputedTimelineOp } from './buildTimelineRequestEntities';
import { buildTimelineRunInput } from './buildTimelineRunInput';
import { buildTruckLookups } from './buildTruckLookups';
import { resolveCardClick } from './cardSelection';
import ConvenienceDispatchAddVehicleDialog from './ConvenienceDispatchAddVehicleDialog';
import ConvenienceDispatchBoard from './ConvenienceDispatchBoard';
import ConvenienceDispatchToolbar from './ConvenienceDispatchToolbar';
import ConvenienceDispatchUnassignedList from './ConvenienceDispatchUnassignedList';
import ConvenienceDispatchVehicleList from './ConvenienceDispatchVehicleList';
import DispatchDetailDialogs from './DispatchDetailDialogs';
import { DragOverlayCard } from './DragOverlayCard';
import {
  DEFAULT_LEFT_PANEL_WIDTH, DEFAULT_RIGHT_PANEL_WIDTH,
  LEFT_PANEL_WIDTH_STORAGE_KEY, RIGHT_PANEL_WIDTH_STORAGE_KEY,
} from './panelWidth';
import { coordKeyOf } from './placeKeys';
import { placeLegRunInRow } from './placeLegRunInRow';
import { resolveCompletionEffects } from './resolveCompletionEffects';
import { resolveConvenienceTariffAvailability } from './resolveConvenienceTariffAvailability';
import {
  buildLegRunMaterialsMap,
  buildRowIdToShiftTruckId,
  buildRowLookup,
  resolveShipmentOrderIdsFromUnassignedIds,
} from './rowLookup';
import { shouldCancelForOverrideChange } from './shouldCancelForOverrideChange';
import { buildPlanningScopeKey, resolveSortedTruckIds } from './truckOrder';
import { MoveTarget, useDispatchDetailActions } from './useDispatchDetailActions';
import { useRotationEditing } from './useRotationEditing';
import { useUnassignedRemovalOptimistic } from './useUnassignedRemovalOptimistic';
import { buildVehicleRows } from './vehicleRowVM';

function isPositiveInteger(parsed: unknown): parsed is number {
  return typeof parsed === 'number' && Number.isInteger(parsed) && parsed > 0;
}

// 一段目の filterVehicleRowGroups に渡す空集合。一段目は mode を
// 「選択中のみ表示する」にしないので、この値は実際には読まれない
// （vehicleFilter.ts が selectedRowIds を見るのはそのモードの分岐だけ）。
// 型が要求するので渡すが、呼び出しの度に new Set() を作らないための定数にしてある
// （アロケーションの衛生。参照が判定に効くわけではない）
const EMPTY_ROW_IDS: ReadonlySet<string> = new Set<string>();

// 中央ボードの DnD センサー設定。距離は左リスト (ConvenienceDispatchVehicleList の
// pointerSensorOptions = 8) と別値なので、名前を分けて共有しない。
//
// Why not インラインのオブジェクトリテラル: useSensor は useMemo の deps に options を
// そのまま並べる (@dnd-kit/core 6.3.1。以下 core.esm.js:190-196 / core.cjs.development.js:197-203)
// ので、毎描画で新しい sensor descriptor になり、useSensors が返す配列も別参照になる
// (esm 198-205 / cjs 205-212)。その配列は useCombineActivators (esm 1933-1944 / cjs 1934-1945)
// → activators → internalContext の deps (esm 3337-3352 / cjs 3334-3349) に入るため、
// 参照が変わるだけで dnd の context 値が変わり、useDroppable / useDraggable / useSortable を
// 呼ぶ全ての子が memo を無視して描き直される
// (= Task 4 のカードと Task 5 の中央の行の memo 境界が実画面で効かなくなる)。
const boardPointerSensorOptions = { activationConstraint: { distance: 3 } };

// 楽観的 UI 更新用の Vehicle 表示フィールド override。truckId 単位でこの値を被せる。

/**
 * 楽観更新で使う地点キー。導出は placeKeys.ts（vehicleRowVM と共有）。
 * ここだけ「配送リストの地点表示」設定に追随し忘れると、ドロップ直後だけカードが
 * 2 枚に割れて再取得で 1 枚に戻る。
 */
const loadingCoordKey = (lr: ShipmentOrderLegRunForPlanEntity): string =>
  coordKeyOf('loading', lr, `lr-${lr.id ?? 'x'}-load`);

const unloadingCoordKey = (lr: ShipmentOrderLegRunForPlanEntity): string =>
  coordKeyOf('unloading', lr, `lr-${lr.id ?? 'x'}-unload`);

// 地点間の想定移動時間（秒）。実距離は考慮せず、仕様上の固定値。
const TRAVEL_SECONDS_BETWEEN_PLACES = 30 * 60;

function addSecondsToDate(date: Date, seconds: number): Date {
  return new Date(date.getTime() + Math.max(0, seconds) * 1000);
}

/**
 * 1 cycle 分の legRuns に対し、積→降 の arrival_at/departure_at を生成する。
 *
 * 仕様:
 * - baseline は呼び出し側で算出して渡す (前 cycle の最終 departure + 30分 / shift 開始時刻 等)
 * - 積み: loading 地点 (placeKey) ごとに集約。同地点の複数 legRun は同一 arrival/departure
 * - 別地点の積み: 前地点 departure + 30分 → 当地点 arrival
 * - 降り 1件目: 最後の積み departure + 30分、以降は積みと同じ規則
 *
 * 時間は配車アルゴリズムで再計算されるため正確である必要はなく、並びの整合性のみが重要。
 */
function computeOpsForCycle(args: {
  stid: number;
  legRuns: ShipmentOrderLegRunForPlanEntity[];
  cycle: number;
  baseline: Date;
}): ComputedTimelineOp[] {
  const { stid, legRuns: groupLegRuns, cycle, baseline } = args;
  if (groupLegRuns.length === 0) return [];

  const result: ComputedTimelineOp[] = [];

  // 積みを地点ごとに集約 (出現順維持)。
  // Why 座標のまま: ここで作るのは送信する arrival/departure。「配送リストの地点表示」は
  // 表示の設定であって、同じ場所での作業を名称ごとに別停車として送る指示ではない
  const loadingGroups: ShipmentOrderLegRunForPlanEntity[][] = [];
  const loadingPlaceIdx = new Map<string, number>();
  groupLegRuns.forEach((lr) => {
    const key = loadingCoordKey(lr);
    const idx = loadingPlaceIdx.get(key);
    if (idx != null) loadingGroups[idx].push(lr);
    else {
      loadingPlaceIdx.set(key, loadingGroups.length);
      loadingGroups.push([lr]);
    }
  });

  let lastDeparture = baseline;
  loadingGroups.forEach((group, groupIdx) => {
    const arrival = groupIdx === 0
      ? baseline
      : addSecondsToDate(lastDeparture, TRAVEL_SECONDS_BETWEEN_PLACES);
    const maxStaying = group.reduce((max, lr) => Math.max(max, lr.loading_staying_seconds || 0), 0);
    const departure = addSecondsToDate(arrival, maxStaying);
    group.forEach((lr, i) => {
      if (lr.id == null) return;
      result.push({
        shift_truck_id: stid,
        order_id: lr.id,
        // 同一地点 2件目以降は 1件目の departure_at に揃える (到着後すぐ出発扱い)
        arrival_at: i === 0 ? arrival : departure,
        departure_at: departure,
        action: '積',
        cycle,
      });
    });
    lastDeparture = departure;
  });

  // 降りを地点ごとに集約（積みと同じ理由で座標のまま）
  const unloadingGroups: ShipmentOrderLegRunForPlanEntity[][] = [];
  const unloadingPlaceIdx = new Map<string, number>();
  groupLegRuns.forEach((lr) => {
    const key = unloadingCoordKey(lr);
    const idx = unloadingPlaceIdx.get(key);
    if (idx != null) unloadingGroups[idx].push(lr);
    else {
      unloadingPlaceIdx.set(key, unloadingGroups.length);
      unloadingGroups.push([lr]);
    }
  });

  unloadingGroups.forEach((group) => {
    const arrival = addSecondsToDate(lastDeparture, TRAVEL_SECONDS_BETWEEN_PLACES);
    const maxStaying = group.reduce((max, lr) => Math.max(max, lr.unloading_staying_seconds || 0), 0);
    const departure = addSecondsToDate(arrival, maxStaying);
    group.forEach((lr, i) => {
      if (lr.id == null) return;
      result.push({
        shift_truck_id: stid,
        order_id: lr.id,
        arrival_at: i === 0 ? arrival : departure,
        departure_at: departure,
        action: '降',
        cycle,
      });
    });
    lastDeparture = departure;
  });

  return result;
}

type Props = {
  startOn: string;
  endOn: string;
  fixedRouteId: number | null;
  fixedRoutes: ConvenienceDispatchFixedRouteEntity[];
  isLoadingFixedRoutes: boolean;
  isApplying: boolean;
  // 全ページを 1 度に取る useQuery ベースになったので、戻り値の型はフックから引く
  // （UseInfiniteQueryResult を書くと fetchPreviousPage などを求められて合わない）
  deliveriesQuery: PlanningsDeliveriesQueryResult;
  legRunsQuery: ShipmentOrderLegRunsQueryResult;
  fixedRouteDetailQuery: UseQueryResult<ConvenienceDispatchFixedRouteDetailEntity>;
  allDeliveriesQuery: UseQueryResult<PlanningsDeliveryWithLegRunEntity[]>;
  /** 表示対象（配送形態・区間種別）。取得条件なので Container が持つ */
  orderAttrFilter: OrderAttrFilter;
  onOrderAttrFilterChange: (next: OrderAttrFilter) => void;
  /**
   * 運賃計算を止めるべきか。Container が算出する（取得条件の debounce 後の値と、
   * 画面の状態である即時値の両方を Container だけが持っているため）。
   * Why not ここで isOrderAttrFilterActive(orderAttrFilter) を呼ぶ: 即時値だけ見ると、
   * 絞り込みを解除した直後（クエリのキーがまだ変わっていない 400ms の間）にガードが
   * 外れ、絞り込み済みのデータのまま運賃計算が実行できてしまう（Codex レビュー P1-a）
   */
  isTariffBlockedByOrderAttrFilter: boolean;
  trucks: PlanningsTruckEntity[];
  drivers: PlanningsDriverEntity[];
  onDateRangeChange: (startOn: string, endOn: string) => void;
  onFixedRouteSelect: (fixedRouteId: number) => void;
  onFixedRouteCreated: (newFixedRouteId: number) => void;
  onFixedRouteCleared: () => void;
  /** toolbar の固定ルート選択経由 (= 呼び出し)。apply のみ、timeline 自動配車は走らせない。
   *  overrideId が指定されれば state の fixedRouteId より優先する（state 更新を待たずに id を指定できる）。 */
  onApplyFixedRoute: (overrideId?: number) => void;
  /** 未割当パネルの「固定ルートに対応づけ」ボタン経由。apply 後に timeline 自動配車を自動発火する。 */
  onAlignToFixedRoute: (overrideId?: number) => void;
  /** Backdrop 文言切り替え用: 'call' = 呼び出し / 'align' = 対応づけ */
  applyMode: 'call' | 'align';
  onNavigate: (path: string) => void;
  /** 未割当リストの32列フィルタへ素通しする値。Map/Presenter.tsx と同じ契約 */
  customInputFieldNames: string[];
  trucksForFilter: { id: number; name: string }[];
  driversForFilter: { id: number; name: string }[];
  companyId: string;
  isLicenseReady: boolean;
  /** license.config.use_algorithm_planning。自動配車ボタンのライセンス層（/map と同じ判定） */
  useAlgorithmPlanning: boolean;
  /**
   * license.config.delivery_list_grouping（表示設定 > 配送リストの地点表示）。
   * groupByName なら同一座標でも納品先名称が違う地点を分けて出す（依頼 log_web_issues#299-2）。
   */
  deliveryListGrouping?: DeliveryListGroupingValues;
  /** 0 は全社表示・null はライセンス未取得。混ぜると嘘のツールチップが出る */
  selectedCompanyId: number | null;
  isMastersReady: boolean;
  /** マスタ取得が失敗したか。Container は必ず渡せるので必須にして伝播漏れを tsc に検出させる */
  isMastersError: boolean;
};

const ConvenienceDispatchesPresenter: FC<Props> = memo(
  ({
    startOn,
    endOn,
    fixedRouteId,
    fixedRoutes,
    isLoadingFixedRoutes,
    isApplying,
    deliveriesQuery,
    legRunsQuery,
    fixedRouteDetailQuery,
    allDeliveriesQuery,
    orderAttrFilter,
    onOrderAttrFilterChange,
    isTariffBlockedByOrderAttrFilter,
    trucks,
    drivers,
    onDateRangeChange,
    onFixedRouteSelect,
    onFixedRouteCreated,
    onFixedRouteCleared,
    onApplyFixedRoute,
    onAlignToFixedRoute,
    applyMode,
    onNavigate,
    customInputFieldNames,
    trucksForFilter,
    driversForFilter,
    companyId,
    isLicenseReady,
    useAlgorithmPlanning,
    deliveryListGrouping = 'groupByAddress',
    selectedCompanyId,
    isMastersReady,
    isMastersError,
  }) => {
    // scope（会社・対象日）キー。並び順の楽観 state・回転編集のリセット判定・詳細パネルの
    // 生存期間判定 (useDispatchDetailActions の scopeKey) で共有する。生成はキー生成関数
    // 1 箇所 (buildPlanningScopeKey) に寄せ、会社を切り替えても前会社の並び順・追加回転・
    // 選択・D&D 楽観的上書きが残らないようにする。
    const planningRangeKey = buildPlanningScopeKey(companyId, startOn, endOn);

    // 表示項目 / 表示列数はリロード後も維持するため localStorage に永続化する。
    // 不正な保存値はバリデータで弾いて初期値に戻す。
    // 実効 Set (displayMetricSet) は useDisplayMetrics が effectiveDisplayMetrics を通して
    // 導出する (契約: 数値指標は常に最低 1 つ有効)。Presenter 側で導出し直さないのは、
    // 呼び忘れで未割当カードだけ実効化されない不具合を再発させないため。
    const [displayMetrics, setDisplayMetrics, displayMetricSet, vehicleDisplayMetricSet] = useDisplayMetrics();

    const [visibleColumns, setVisibleColumns] = usePersistedJSON<number>(
      'convenienceDispatch.visibleColumns',
      10,
      isPositiveInteger,
    );
    const [isRightPanelOpen, setIsRightPanelOpen] = useState(true);

    // 左右カラムの幅。案件名や納品先が読み切れないので可変にし、変えた幅は次回も再現する
    // (log_web_issues#312。/map の #299 と同じ仕組みを共有モジュールから使う)
    const [leftPanelWidth, setLeftPanelWidth] = usePersistedJSON<number>(
      LEFT_PANEL_WIDTH_STORAGE_KEY,
      DEFAULT_LEFT_PANEL_WIDTH,
      isPanelWidth,
    );
    const [rightPanelWidth, setRightPanelWidth] = usePersistedJSON<number>(
      RIGHT_PANEL_WIDTH_STORAGE_KEY,
      DEFAULT_RIGHT_PANEL_WIDTH,
      isPanelWidth,
    );
    // 右ペインの幅には開閉アニメーションの transition が乗っている。ドラッグ中も効いたままだと
    // 指の動きに 0.2s 遅れて追いかけてくるので、掴んでいる間だけ切る
    const [rightPanelResizing, setRightPanelResizing] = useState(false);

    // 左右＋中央を並べている器の幅。保存幅がこれを超えるときは描画時に落とす（fitPanelWidths）
    const paneContainerRef = useRef<HTMLDivElement>(null);
    const [paneContainerWidth, setPaneContainerWidth] = useState(0);
    // Why useLayoutEffect: 初回描画で幅が 0 のままだと、器に収まらない保存幅がそのまま
    // 1 フレーム描かれてから縮む（はみ出しが一瞬見える）
    useLayoutEffect(() => {
      const element = paneContainerRef.current;
      if (!element) return undefined;
      const measure = () => setPaneContainerWidth(element.getBoundingClientRect().width);
      measure();
      // ResizeObserver ならサイドナビの開閉のように window の resize を伴わない変化も拾える。
      // jsdom には無いので、その環境では window の resize で代替する
      if (typeof ResizeObserver === 'undefined') {
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
      }
      const observer = new ResizeObserver(measure);
      observer.observe(element);
      return () => observer.disconnect();
    }, []);

    // 保存幅が器に収まらないときは描画時に落とす。ドラッグ開始時の上限だけだと
    // 「右を広げる → 畳む → 左を広げる → 右を開き直す」で中央ボードが隠れる
    const { left: fittedLeftPanelWidth, right: fittedRightPanelWidth } = useMemo(
      () => fitPanelWidths({
        containerWidth: paneContainerWidth,
        left: leftPanelWidth,
        right: rightPanelWidth,
        rightOpen: isRightPanelOpen,
      }),
      [paneContainerWidth, leftPanelWidth, rightPanelWidth, isRightPanelOpen],
    );

    const deliveries = useMemo(
      () => deliveriesQuery.data?.pages.flatMap((page) => page.data) ?? [],
      [deliveriesQuery.data],
    );

    // 車両リストの props / VM は運賃を持たないため、粗利は Presenter で算出して map で渡す。
    const grossProfitByTruckId = useMemo(() => buildGrossProfitByTruckId(deliveries), [deliveries]);

    const legRuns = useMemo(
      () => legRunsQuery.data?.pages.flatMap((page) => page.data) ?? [],
      [legRunsQuery.data],
    );

    void allDeliveriesQuery;

    // `/plannings/.../deliveries` の `shipmentOrderLegRuns[]` は compact=true でシリアライズ
    // されており、数量 / 備考 / 作業秒 / 指定時間 などが抜けている。
    // 一方 `/api/v5/shipment_order_leg_runs` は非 compact なので、id で突き合わせて補完する。
    const legRunMaterialsMap = useMemo(() => buildLegRunMaterialsMap(legRuns), [legRuns]);

    // deliveries のコピーを作って legRun を補完してから buildVehicleRows に渡す
    const enrichedDeliveries = useMemo(() => {
      if (legRunMaterialsMap.size === 0) return deliveries;
      return deliveries.map((d) => ({
        ...d,
        shipmentOrderLegRuns: d.shipmentOrderLegRuns.map((lr) => {
          if (lr.id == null) return lr;
          const rich = legRunMaterialsMap.get(lr.id);
          if (!rich) return lr;
          // 非 compact 側 (rich) に存在するフィールドで補完する。
          // delivery 由来の compact 値 (lr) を base にし、rich の値で上書き。
          return { ...lr, ...rich };
        }),
      }));
    }, [deliveries, legRunMaterialsMap]);

    // 未割当 legRun（assigned=false）を buildVehicleRows の差分判定へ渡す。
    // 固定ルートにある店舗が未割当にも存在する場合は「本日なし」を出さないため。
    const unassignedLegRunsForVM = useMemo(
      () => legRuns.filter((lr) => !lr.assigned),
      [legRuns],
    );

    // VehicleRowVM を buildVehicleRows で組み立てる（既存のデータパイプラインは維持）
    const sourceRowsRaw = useMemo(
      () => buildVehicleRows(
        enrichedDeliveries,
        fixedRouteId != null ? fixedRouteDetailQuery.data : null,
        unassignedLegRunsForVM,
        trucks,
        drivers,
        deliveryListGrouping,
      ),
      [enrichedDeliveries, fixedRouteId, fixedRouteDetailQuery.data, unassignedLegRunsForVM, trucks, drivers,
        deliveryListGrouping],
    );


    // 汎用確認ダイアログ・snackbar・resetting mutation は useRotationEditing が直接参照するため、
    // ここへ前倒しして定義する（TDZ 回避。後段の全参照はスコープ内に残る）。
    const [confirmDialog, setConfirmDialog] = useState<{
      title: string;
      description: string;
      onConfirm: () => void | Promise<void>;
    } | null>(null);

    const { enqueueSnackbar } = useSnackbar();
    // notify を安定化して前倒しする。useRotationEditing / useShiftDeletion にインライン arrow
    // `(message, variant) => enqueueSnackbar(...)` を渡すと毎レンダー新しい関数になり、それを
    // 依存に持つ handleDeleteRotation / requestDeleteShift まで作り直されて左のトラックリストの
    // memo が破れる（/map の e9549dc2 と同じ手口で安定化する）。
    const notify = useCallback(
      (message: string, variant?: 'success' | 'error' | 'info') => { enqueueSnackbar(message, { variant }); },
      [enqueueSnackbar],
    );
    const {
      operationBulkResetting, timelinePlanning, normalPlanning, transferPlanning, swapPlanning,
    } = useMutationPlanningV5();
    // useMutation の戻り値は毎レンダー新しいオブジェクト。mutateAsync は安定なので、依存には
    // こちらを使う（useShiftDeletion.ts の removeMutateAsync と同じ手口。オブジェクトを直接
    // 依存にすると、それに connect した useCallback が毎回作り直される上、
    // react-hooks/exhaustive-deps がメンバー呼び出しの基点オブジェクトも要求してしまう）
    const operationBulkResettingMutateAsync = operationBulkResetting.mutateAsync;
    // resetting 成功後、react-query キャッシュを直接書き換えて即座に UI 反映する（再取得なし）。
    // 実体は useApplyResetToCaches に集約（カード配車・地図配車の両方で共有するため中立化済み）。
    // 下の resetLegRuns（useRotationEditing への注入）が依存に取るため、通常の定義位置より前倒しする。
    const applyResetToCaches = useApplyResetToCaches();

    // getRowGroups を安定させる。inline arrow `() => serverRowGroups` は毎レンダー新しい関数に
    // なり、それを依存に持つ handleDeleteRotation が作り直されて左のトラックリストの memo が
    // 破れる。serverRowGroups は useRotationEditing の出力 (sourceRows) に依存するため、
    // この時点ではまだ計算できない。ref に読み替え、実体の同期は算出後の useLayoutEffect
    // （serverRowGroups の useMemo の直後）で行う（busyStateRef / detailKeyRef と同じ手口）。
    const serverRowGroupsRef = useRef<VehicleRowGroup[]>([]);
    const getRowGroups = useCallback(() => serverRowGroupsRef.current, []);

    // resetLegRuns を安定させる。呼んでいるのは mutateAsync だけなので、依存は mutation
    // オブジェクト丸ごとではなく mutateAsync に絞る（useMutation は毎レンダー新しいオブジェクトを
    // 返すため、丸ごと依存にすると handleDeleteRotation が毎回作り直される）。
    const resetLegRuns = useCallback(async (ids: number[]) => {
      await operationBulkResettingMutateAsync(ids);
      applyResetToCaches(ids);
    }, [operationBulkResettingMutateAsync, applyResetToCaches]);

    // 回転編集（追加/削除・仮想行マージ・placeholder 非表示）は useRotationEditing に集約。
    const {
      sourceRows,
      applyHiddenFilter,
      handleAddRotation,
      handleDeleteRotation,
    } = useRotationEditing({
      sourceRowsRaw,
      getRowGroups,
      resetAllKey: planningRangeKey,
      hiddenResetKey: `${fixedRouteId}`,
      resetLegRuns,
      confirm: setConfirmDialog,
      notify,
    });

    // 自動配車の running 状態は controller が持つ（日付変更時のリセットも controller 内部の
    // scope 変更 effect が行う。回転編集の extraRotations / hiddenPlaceholderRotations リセットは
    // useRotationEditing 内で resetAllKey により実施）

    // truckId → VehicleInfoExtra マップ構築
    const truckCapacityMap = useMemo<Map<number, number | null>>(() => {
      const map = new Map<number, number | null>();
      for (const d of deliveries) {
        if (!map.has(d.truckId)) {
          map.set(d.truckId, d.maximumLoadingCapacityQuantity);
        }
      }
      return map;
    }, [deliveries]);

    // truckId / driverId → マスタ実体
    const truckMasterMap = useMemo(() => {
      const m = new Map<number, PlanningsTruckEntity>();
      for (const t of trucks) m.set(t.id, t);
      return m;
    }, [trucks]);
    const driverMasterMap = useMemo(() => {
      const m = new Map<number, PlanningsDriverEntity>();
      for (const d of drivers) m.set(d.id, d);
      return m;
    }, [drivers]);
    // truckId → その車両に割り当てられている代表的な driverId (deliveries 側から抽出)
    const truckDriverMap = useMemo(() => {
      const m = new Map<number, number>();
      for (const d of deliveries) {
        if (!m.has(d.truckId)) m.set(d.truckId, d.driverId);
      }
      return m;
    }, [deliveries]);

    const vehicleExtraMap = useMemo<Map<number, VehicleInfoExtra>>(() => {
      const detail = fixedRouteDetailQuery.data;
      const map = new Map<number, VehicleInfoExtra>();

      // truck master からの fallback
      // PlanningsTruckEntity は
      //   - 重量: maximumLoadingCapacityWeightForCalculation (g) のみ
      //   - 体積: loadingPlatformVolumeMm3 (mm³) のみ
      // を持つため、単位変換して採用する
      // V3Plans / V3Timeline と同様に temporary truck は「車両未設定」、temporary driver は「ドライバー未設定」表示
      // 当日勤務時間と労働可能時間 (shift_truck 由来) を truckId 単位で引けるように。
      // ループを純粋関数に切り出してテスト可能にしている。
      const shiftTimeByTruckId = buildShiftTimeByTruckId(deliveries);
      // 色は「最初の非 null」、shift_truck 系は「最初の delivery」と規則が違うので分ける
      const truckColorByTruckId = buildTruckColorMap(deliveries);
      const { shiftTruckByTruckId, shiftTruckNumberByTruckId } = buildTruckLookups(deliveries);

      const makeExtra = (truckId: number, capacityQuantity: number | null): VehicleInfoExtra => {
        const truck = truckMasterMap.get(truckId);
        const driverId = truckDriverMap.get(truckId) ?? null;
        const driver = driverId != null ? driverMasterMap.get(driverId) : undefined;
        // truck.license_plate_value = "車両名" として表示 (例: "5号車" / "品川あ100-110")
        const truckName = truck?.temporary ? TRUCK_UNSET_LABEL : (truck?.licensePlateValue || null);
        // 車番の優先順位: shift_truck.truck_number → (adapter で) leg_run.truck_number → truck.truck_number
        const shiftTruckNumber = shiftTruckNumberByTruckId.get(truckId) ?? null;
        const masterTruckNumber = truck?.temporary ? null : (truck?.truckNumber ?? null);
        const driverName = driver?.temporary ? 'ドライバー未設定' : (driver?.name ?? null);
        const shiftTime = shiftTimeByTruckId.get(truckId);
        const maxG = truck?.maximumLoadingCapacityWeightForCalculation ?? 0;
        const maxMm3 = truck?.loadingPlatformVolumeMm3 ?? 0;
        return {
          partnerCompanyName: truck?.temporary ? null : truck?.partnerCompanyName ?? null,
          truckName,
          shiftTruckNumber,
          masterTruckNumber,
          truckLabel: truckName,
          defaultDriverName: driverName,
          shiftStart: shiftTime?.start ?? null,
          shiftEnd: shiftTime?.end ?? null,
          workingAvailableSeconds: shiftTime?.workingAvailableSeconds ?? null,
          driverId,
          shiftTruckId: shiftTruckByTruckId.get(truckId) ?? null,
          capacityCases: null,
          capacityQuantity,
          capacityKg: maxG > 0 ? maxG / 1000 : null,
          capacityM3: maxMm3 > 0 ? maxMm3 / 1e9 : null,
          truckColor: truckColorByTruckId.get(truckId) ?? null,
          partnerCompanyId: truck?.partnerCompanyId ?? null,
          partnerCompanyColor: truck?.temporary ? null : truck?.partnerCompanyColor ?? null,
          truckKlass: truck?.klass ?? null,
        };
      };

      if (detail) {
        for (const v of detail.vehicles) {
          if (v.truckId != null) {
            const capacityQuantity = truckCapacityMap.has(v.truckId)
              ? truckCapacityMap.get(v.truckId) ?? null
              : (v.capacityQuantity ?? null);
            const fallback = makeExtra(v.truckId, capacityQuantity);
            // fallback を spread して、固定ルート由来の値がある項目だけ上書きする。
            // 明示列挙にすると VehicleInfoExtra へフィールドを足したとき
            // 固定ルート詳細がある会社だけ値が落ちる (labor 時間で実際に踏んだ落とし穴)。
            map.set(v.truckId, {
              ...fallback,
              partnerCompanyName: v.partnerCompanyName ?? fallback.partnerCompanyName,
              truckLabel: v.truckLabel ?? fallback.truckLabel,
              defaultDriverName: v.defaultDriverName ?? fallback.defaultDriverName,
              capacityCases: v.capacityCases,
              capacityQuantity,
            });
          }
        }
      }
      // fixed route 未選択車両は truck master のみから埋める
      for (const [truckId, capacityQuantity] of truckCapacityMap) {
        if (!map.has(truckId)) {
          map.set(truckId, makeExtra(truckId, capacityQuantity ?? null));
        }
      }
      return map;
    }, [fixedRouteDetailQuery.data, truckCapacityMap, truckMasterMap, driverMasterMap, truckDriverMap]);

    // 1 台に複数のドライバーが乗る勤務があるので、行ごとの名前を引けるようにする
    // （車両の代表情報 vehicleExtraMap は truckId 単位なので、勤務ごとの名前はここから引く）
    const driverNameById = useMemo(() => new Map(drivers.map((d) => [d.id, d.name])), [drivers]);

    // VehicleRowVM → VehicleRowGroup (= Vehicle + RotationRow[]) に変換（サーバ由来）
    // 前回の結果を持ち回して、入力 VM も補助 Map も変わっていないグループは同じオブジェクトを
    // 返す（回転を 1 台に足したときに全車両の行が描き直されるのを止める）
    const rowGroupsCacheRef = useRef<VehicleRowGroupsCache | null>(null);
    rowGroupsCacheRef.current ??= createVehicleRowGroupsCache();
    const rowGroupsCache = rowGroupsCacheRef.current;
    const serverRowGroups = useMemo(
      () => buildVehicleRowGroups(sourceRows, vehicleExtraMap, driverNameById, rowGroupsCache),
      [sourceRows, vehicleExtraMap, driverNameById, rowGroupsCache],
    );
    // serverRowGroupsRef（getRowGroups の安定化用、:610 相当）への同期。render 中の代入ではなく
    // useLayoutEffect で行う（busyStateRef / detailKeyRef と同じパターン）
    useLayoutEffect(() => { serverRowGroupsRef.current = serverRowGroups; }, [serverRowGroups]);
    // 未割当リストは assigned=false (= delivery に紐付いていない) legRun のみを表示
    const serverUnassigned = useMemo<UnassignedItem[]>(
      () => legRuns.filter((lr) => !lr.assigned).map((lr, idx) => toUnassignedItem(lr, idx)),
      [legRuns],
    );

    // D&D 用のローカル上書き状態
    // 各 row の cards 配列、未割当 item 配列をローカルで管理する。
    // サーバデータが変わる度に再初期化される。
    const [overrideRowCards, setOverrideRowCards] = useState<Map<string, DispatchCard[]>>(new Map());
    const [overrideUnassigned, setOverrideUnassigned] = useState<UnassignedItem[] | null>(null);
    // 自動配車中に追加操作が入ったかどうか。古い job の完了/refetch で
    // 操作途中の override を消さないため、state ではなく同期的に読める ref で持つ。
    const hasQueuedOverrideChangeRef = useRef(false);
    // 自動配車に送る直前の未割当一覧。停止したときに消した案件を戻すために覚える
    const unassignedBeforeRunRef = useRef<UnassignedItem[] | null>(null);
    const overrideStateRef = useRef({ overrideRowCount: 0, hasOverrideUnassigned: false });
    useEffect(() => {
      overrideStateRef.current = {
        overrideRowCount: overrideRowCards.size,
        hasOverrideUnassigned: overrideUnassigned !== null,
      };
    }, [overrideRowCards, overrideUnassigned]);

    // 楽観的 UI 更新用のローカル上書き (車両名 / 車番 / ドライバー名)。key = 元の truckId。
    // mutation 発火と同時に表示を切り替え、refetch 完了後にサーバ値に置き換える。
    // refetch 後にサーバが同じ値を返せば override を消しても見た目は変わらない (ちらつき無し)。
    const {
      overrides: vehicleOverrides, setVehicleOverride, clearVehicleOverride,
    } = useVehicleDisplayOverrides();

    // 楽観表示が乗っている勤務 = 直前に操作して refetch 待ち。以前はそのカードを緑にしていたが、
    // 色が強すぎて画面が読めないため、/map と同じく右下のトーストで伝える
    const isApplyingVehicleChange = vehicleOverrides.size > 0;

    // サーバデータ変化時のみローカル上書きをリセット。
    // extraRotations（回転追加）の変化では serverRowGroups も再計算されるが、
    // それでリセットしてしまうと D&D で置いた案件が消える。
    // サーバ純粋由来である sourceRowsRaw / serverUnassigned を依存にする。
    useEffect(() => {
      const overrideState = overrideStateRef.current;
      if (!shouldResetOverrideAfterServerRefresh({
        overrideRowCount: overrideState.overrideRowCount,
        hasOverrideUnassigned: overrideState.hasOverrideUnassigned,
        hasQueuedOverrideChange: hasQueuedOverrideChangeRef.current,
      })) return;
      // 既に空なら参照も変えない（内容が変わらないのに setState すると、override 監視
      // effect の参照比較が「ユーザーが編集した」と誤読し、実行中の run を自己
      // キャンセルする。whole-branch レビュー C-1）
      setOverrideRowCards((prev) => (prev.size === 0 ? prev : new Map()));
      setOverrideUnassigned((prev) => (prev === null ? prev : null));
    }, [sourceRowsRaw, serverUnassigned]);

    // 実表示用の rowGroups / unassigned。
    // override が無関係な行・グループの参照まで作り直さないよう、純関数に切り出している
    // （切り出しの理由は applyOverrideRowCards のコメント参照）
    const rowGroups = useMemo(
      () => applyOverrideRowCards(serverRowGroups, overrideRowCards),
      [serverRowGroups, overrideRowCards],
    );

    const unassigned = overrideUnassigned ?? serverUnassigned;

    // rowId ベースの選択状態
    const [selectedRowIds, setSelectedRowIds] = useState<Set<string>>(new Set());
    /**
     * 車両リストの絞り込み。/map と同じで持ち越さない。
     *
     * Why not localStorage: 前回の絞り込みが復元され、次に開いたとき車両が 1 台も
     * 出ない画面から始まる（回転を全部外した状態や、対象日にいないグループの選択が残る）。
     */
    const [truckDisplayStatus, setTruckDisplayStatus] = useState<VehicleFilterMode>('すべて表示');
    const [rotations, setRotations] = useState<RotationFilterValue[]>([...ALL_ROTATION_FILTERS]);
    const [constraintViolationOnly, setConstraintViolationOnly] = useState(false);

    // 対象日だけでなく会社切替でも選択行をリセットする (planningRangeKey は
    // buildPlanningScopeKey で companyId を含む)。
    useEffect(() => {
      setSelectedRowIds(new Set());
    }, [planningRangeKey]);

    const onRowToggle = useCallback((rowId: string) => {
      setSelectedRowIds((prev) => {
        const next = new Set(prev);
        if (next.has(rowId)) next.delete(rowId);
        else next.add(rowId);
        return next;
      });
    }, []);

    // 未割当の同一地点タグ (#318) から車両を選んだら、車両リストをその行まで動かす。
    // 行のクリック (onRowToggle) では動かさない。その場で見えているのに跳ねる
    // （/map の onMarkerRowClick と同じ考え方）。
    const { registerRow, scrollToRow } = useScrollToRow();

    /**
     * バッジを押すと n 台ぶん来るので、選択の追加は 1 回の setState で済ませ、
     * スクロールは**先頭の行**へ（最後の行へ飛ぶと押した順と逆に見える）。
     */
    const onSelectVehicleRows = useCallback((rowIds: readonly string[]) => {
      if (rowIds.length === 0) return;
      // トグルにしない。タグから選ぶのは「そこを見たい」意図しかないので、
      // すでに選ばれていたときに解除すると意図と逆になる
      setSelectedRowIds((prev) => {
        // 全部すでに選ばれていれば同じ Set を返す。バッジは何度でも押せるので、
        // 押すたびに参照が変わると車両リストが無駄に描き直される
        if (rowIds.every((rowId) => prev.has(rowId))) return prev;
        const next = new Set(prev);
        rowIds.forEach((rowId) => next.add(rowId));
        return next;
      });
      scrollToRow(rowIds[0]);
    }, [scrollToRow]);

    // 表示するトラックグループ (V3Plans の PlanningsGroupEntity 仕組み)。
    // V3Plans Component.tsx と同様、先頭に id=0 「すべて表示する」を持つ合成エントリを prepend し、
    // index ベースで多選択する (TruckDisplayStatusMenu の規約)。
    const { data: planningGroupsData } = useQueryPlanningGroups();
    const allDisplayedTruckIds = useMemo(
      () => rowGroups
        .map((g) => Number(g.vehicle.id.replace('truck-', '')))
        .filter((id) => Number.isFinite(id)),
      [rowGroups],
    );
    const truckGroupEntitiesWithAll = useMemo<PlanningsGroupEntity[]>(
      () => buildTruckGroupsWithAll(planningGroupsData ?? [], allDisplayedTruckIds),
      [planningGroupsData, allDisplayedTruckIds],
    );

    // 選択は group id で覚える（/map と同じ hook）。初期はすべてのチェックが付いた状態で、
    // 表示条件と同じく持ち越さない
    const {
      selectedIndices: selectedTruckGroupIndices,
      setSelectedIndices: setSelectedTruckGroupIndices,
      allowedTruckIds,
    } = useTruckGroupSelection(truckGroupEntitiesWithAll);

    // 車両表示順 (PlanningTrucksPresenter と同じ仕組み)。ドラッグで即座に並びを変える楽観的更新を
    // するために、サーバ確定前の order を local state で保持する。range が変わったらリセット。
    const canSortTruckDisplayOrder = startOn === endOn;
    const truckDisplayOrderMutation = useMutationPlanningTruckDisplayOrders(startOn, endOn);
    // useMutation の戻り値は毎レンダー新しいオブジェクト。mutate は安定なので、依存には
    // こちらを使う（useShiftDeletion.ts の removeMutateAsync と同じ手口）
    const truckDisplayOrderMutationMutate = truckDisplayOrderMutation.mutate;
    const [sortedTruckIdsState, setSortedTruckIdsState] = useState<{
      rangeKey: string;
      ids: number[];
    }>({ rangeKey: '', ids: [] });
    const sortedTruckIds = useMemo(
      () => resolveSortedTruckIds(sortedTruckIdsState, planningRangeKey),
      [planningRangeKey, sortedTruckIdsState],
    );

    // フィルタ: 車両表示ステータス (V3Plans 互換) + トラックグループ。両方適用 (AND)。
    //   配車済み車両のみ表示 : truck の rows に operation を持つ row が 1 つでもあれば表示
    //   未配車の車両のみ表示  : 上記の逆 (= 配車されていない truck)
    //   選択中のみ表示する    : 選択中の row のみ表示
    // 積載以外の制約違反（勤務時間・指定時間・バース予約）。V3Plans と同じ判定・文言で出す。
    // 「制約違反のある車両のみ」の絞り込みも読むので filteredRowGroups より前に置く
    const constraintAlertsByRowId = useMemo(
      () => buildConstraintAlertsByRowId(rowGroups, deliveries),
      [rowGroups, deliveries],
    );

    // 回転行ごとの積載量（同時に積んでいる量の最大）。勤務全体の operation を走査するので、
    // 絞り込み前の全グループから作る（絞ったあとの行で作ると、隠れている回転で積んだぶんが
    // 消えて表示中の行の積載量まで変わる）
    const peakLoadByRowId = useMemo(
      () => buildPeakLoadByRowId(rowGroups),
      [rowGroups],
    );

    // 選択に依存しない絞り込みまで。ここに selectedRowIds を入れないことが肝で、
    // 入れると行を選ぶたびに filter/map が新しい配列を作り、中央ボードが再描画される
    const filteredRowGroupsBase = useMemo(() => {
      // 楽観的更新の override を Vehicle 表示フィールドにマージ (truckName / plateNumber / driverName)
      let result = applyVehicleOverrides(rowGroups, vehicleOverrides);
      // ユーザーが × ボタンで非表示にした placeholder rotation を除外
      // (実配送 row は同じ rotationNumber でも除外しない: adapter.ts 参照)
      result = applyHiddenFilter(result);
      if (allowedTruckIds) {
        result = result.filter((g) => {
          const truckIdNum = Number(g.vehicle.id.replace('truck-', ''));
          return Number.isFinite(truckIdNum) && allowedTruckIds.has(truckIdNum);
        });
      }
      // 絞り込みは /map と共有する（片方だけ直すと同じ設定で見える台数が食い違う）。
      // 選択で絞るのは次段なので、「選択中のみ表示する」のとき**だけ**ここでは
      // 「すべて表示」として扱う（他のモードは mode をそのまま素通しする）。
      //
      // 車両単位で落とす 2 つ（表示対象・制約違反のある車両のみ）は、
      // filterVehicleRowGroups の中で「行を絞ったあとの行集合」に対して判定される。
      // 選択が行を絞るモードでこれをこの段で掛けると順序が入れ替わり、/map の 1 回呼びと
      // 見える台数が食い違う（違反の無い回転だけ選んでも車両が残る／隠した案件を積まない
      // 回転だけ選んでも車両ごと消える）。そのモードのときだけ次段へ回す。
      //
      // Why not 常に次段へ回す: 車両単位の絞り込みが 1 つでも効いているとき
      // （表示対象が有効、または「制約違反のある車両のみ」が ON）、次段の filter が
      // 選択のたびに新しい配列を作り（Array.prototype.filter は常に新配列を返す）、
      // 通常モードでも行選択で中央ボードが再描画に戻る。2 つとも無効なら
      // filterVehicleRowGroups は入力をそのまま返すので影響は無い。
      // 他のモードでは選択が行を絞らないので、この段で全行に対して判定しても
      // 1 回呼びと同じ結果になる。
      const deferGroupFilters = truckDisplayStatus === '選択中のみ表示する';
      result = filterVehicleRowGroups(result, {
        mode: deferGroupFilters ? 'すべて表示' : truckDisplayStatus,
        selectedRowIds: EMPTY_ROW_IDS,
        rotations,
        constraintViolationOnly: deferGroupFilters ? false : constraintViolationOnly,
        constraintAlertsByRowId,
        peakLoadByRowId,
        orderAttrFilter: deferGroupFilters ? undefined : orderAttrFilter,
      });
      // 楽観的更新の並び順を適用 (sortedTruckIds は drag 直後にセットされる)。
      // サーバ完了で query invalidate → rowGroups 再構築までの間、ドラッグ後の表示順を保持する。
      if (sortedTruckIds.length > 0) {
        const orderIndex = new Map(sortedTruckIds.map((id, idx) => [id, idx]));
        const truckIdOfGroup = (g: VehicleRowGroup): number | null => {
          const n = Number(g.vehicle.id.replace('truck-', ''));
          return Number.isFinite(n) ? n : null;
        };
        result = [...result].sort((a, b) => {
          const aId = truckIdOfGroup(a);
          const bId = truckIdOfGroup(b);
          const aIdx = aId != null ? orderIndex.get(aId) ?? -1 : -1;
          const bIdx = bId != null ? orderIndex.get(bId) ?? -1 : -1;
          if (aIdx === -1 && bIdx === -1) return 0;
          if (aIdx === -1) return 1;
          if (bIdx === -1) return -1;
          return aIdx - bIdx;
        });
      }
      return result;
    }, [rowGroups, truckDisplayStatus, allowedTruckIds, vehicleOverrides,
      applyHiddenFilter, sortedTruckIds, rotations, constraintViolationOnly,
      constraintAlertsByRowId, peakLoadByRowId, orderAttrFilter]);

    // 「選択中のみ表示する」だけが選択を読む。他のモードでは前段の配列をそのまま返す
    // （参照を作り直すと memo 済みのボードが毎回再描画される）。
    // 車両単位の 2 つを一緒に渡すのは、選択で絞ったあとの行集合で判定させるため（前段の説明）
    const filteredRowGroups = useMemo(() => {
      if (truckDisplayStatus !== '選択中のみ表示する') return filteredRowGroupsBase;
      return filterVehicleRowGroups(filteredRowGroupsBase, {
        mode: '選択中のみ表示する',
        selectedRowIds,
        // 回転は前段で適用済み。渡すと displayRotationNumber を付け直す無駄が増える
        rotations: undefined,
        constraintViolationOnly,
        constraintAlertsByRowId,
        peakLoadByRowId,
        orderAttrFilter,
      });
    }, [filteredRowGroupsBase, truckDisplayStatus, selectedRowIds, constraintAlertsByRowId,
      peakLoadByRowId, constraintViolationOnly, orderAttrFilter]);

    // 全選択の対象は「画面に見えている行」。絞り込み後の filteredRowGroups から取る。
    // rowGroups から取ると、絞り込みで隠れている車両まで選ばれ、選択件数が
    // 当日の全車両数になる（例: 画面 62 台に対し 104 台選択）。
    // 除外規則は /map と共有する（片方だけ直すと同じ操作で選択件数が食い違う）
    const allRowIds = useMemo(() => collectSelectableRowIds(filteredRowGroups), [filteredRowGroups]);
    const allSelected = allRowsSelected(allRowIds, selectedRowIds);

    const onSelectAll = useCallback(() => {
      setSelectedRowIds(new Set(allRowIds));
    }, [allRowIds]);
    const onDeselectAll = useCallback(() => setSelectedRowIds(new Set()), []);

    // 既存車両/ドライバー（AddVehicleDialog 用）
    const existingTruckIds = useMemo(() => new Set(sourceRows.filter((r) => !r.isPlaceholderRow).map((r) => r.truckId)), [sourceRows]);
    const existingDriverIds = useMemo(() => new Set(deliveries.map((d) => d.driverId)), [deliveries]);

    const [addVehicleDialogOpen, setAddVehicleDialogOpen] = useState(false);
    const [fixedRouteSelectorOpen, setFixedRouteSelectorOpen] = useState(false);

    // 汎用確認ダイアログ・snackbar・resetting mutation は useRotationEditing 呼び出しのため前段(:592)へ移動済み。
    const { restoreSplitLegRun } = useMutationShipmentOrderLegRun();
    // handleRestoreSplit の依存を安定させる。useMutation の戻り値は毎レンダー新規オブジェクトで、
    // legRunsQuery は Container から渡される prop（Container 再レンダーで新規になり得る）。
    // 呼んでいるメンバ (mutate / refetch) はどちらも安定なので、それだけを依存に使う
    // （useShiftDeletion.ts の removeMutateAsync と同じ手口）
    const restoreSplitLegRunMutate = restoreSplitLegRun.mutate;
    const legRunsQueryRefetchForRestoreSplit = legRunsQuery.refetch;

    const handleTruckOrderChange = useCallback((visibleNextIds: number[]) => {
      if (!canSortTruckDisplayOrder) return;
      // visibleNextIds はフィルタ範囲内 (filteredRowGroups) のみの並び順。
      // API は受け取った truck_ids だけで version を再作成するため、フィルタ外・delivery なしの
      // 車両も含めた全車両 (trucks) を base にマージして送る。フィルタ範囲外の車両は base の
      // 元の位置を維持し、フィルタ範囲内の slot だけを visibleNextIds の順で埋める。
      // 例) base = [1, 2, 3, 4, 5], visibleNext = [5, 1, 3] (filter で 2,4 は非表示)
      //   → merged = [5, 2, 1, 4, 3]
      //
      // base 選定:
      //  - 既に DnD 後の楽観的 state (sortedTruckIds) があればそれを優先 (連続 drag 対応)
      //  - そうでなければ trucks マスタを API と同じ規則 (displayOrder bucket → sortOrder) で
      //    並べた順を使う (= rowGroups は delivery 由来で未配車車両が含まれないので使えない)
      const baseTruckIds = sortedTruckIds.length > 0
        ? sortedTruckIds
        : trucks
          .slice()
          .sort(compareTrucksByDisplayOrder)
          .map((t) => t.id);

      const visibleSet = new Set(visibleNextIds);
      let pointer = 0;
      const mergedIds = baseTruckIds.map((id) => {
        if (visibleSet.has(id) && pointer < visibleNextIds.length) {
          const next = visibleNextIds[pointer];
          pointer += 1;
          return next;
        }
        return id;
      });
      // 念のため: base にない visibleNextIds (= 新規 truck など) は末尾に append
      while (pointer < visibleNextIds.length) {
        mergedIds.push(visibleNextIds[pointer]);
        pointer += 1;
      }

      // 楽観的更新: ドラッグ直後に並びを反映する
      setSortedTruckIdsState({ rangeKey: planningRangeKey, ids: mergedIds });
      truckDisplayOrderMutationMutate(mergedIds, {
        onError: () => {
          // ロールバック: rangeKey をクリアしてサーバ真値からの再構築を促す
          setSortedTruckIdsState({ rangeKey: '', ids: [] });
          enqueueSnackbar('車両表示順の保存に失敗しました', { variant: 'error' });
        },
      });
      // 依存は truckDisplayOrderMutationMutate（mutate のみ、:965 相当で切り出し済み）。
      // isPending 等は読んでいない。useMutation の戻り値オブジェクトは毎レンダー新規になる
      // ため、丸ごと依存にすると左のトラックリストに渡る onTruckOrderChange が毎回作り直され
      // memo が破れる（/map の e9549dc2 と同じ手口）
    }, [canSortTruckDisplayOrder, enqueueSnackbar, planningRangeKey, sortedTruckIds, trucks, truckDisplayOrderMutationMutate]);

    // 未割当案件があるときの「理由 / ヒント」表示用。polling 完了時に session_id を保持する。
    const [notAllocSessionId, setNotAllocSessionId] = useState<string | null>(null);
    const [suggestionDialogOpen, setSuggestionDialogOpen] = useState(false);
    const [showNotAllocSuggestionSnackbar, setShowNotAllocSuggestionSnackbar] = useState(false);
    const { data: notAllocSuggestions } = useQueryPlanningsSuggestions(notAllocSessionId ?? '');
    const { data: notAllocReasonsData } = useQueryPlanningsNotAllocReasons(notAllocSessionId ?? '');
    // API の order_id は常に leg_run.id なので、そのまま key にする（理由は関数側のコメント）
    const notAllocReasonsByOrderId = useMemo(
      () => buildNotAllocReasonsByOrderId(notAllocReasonsData),
      [notAllocReasonsData],
    );
    // NotAllocReasonPresenter が要求する型は PlanningsDeliveryEntity[] だが
    // 使われるのは id / truckId / driverId のみ。キャストで渡す。
    const notAllocDeliveriesForReason = useMemo(
      () => deliveries as unknown as PlanningsDeliveryEntity[],
      [deliveries],
    );
    useEffect(() => {
      setShowNotAllocSuggestionSnackbar(
        (notAllocSuggestions?.length ?? 0) > 0,
      );
    }, [notAllocSuggestions]);
    const handleSuggestionDialogClose = useCallback((_e: MouseEvent<HTMLElement>) => {
      setSuggestionDialogOpen(false);
    }, []);

    // 配車のヒント (NotAllocSuggestion snackbar) を即時クリアする。
    // 新しい timeline/normal/transfer planning を開始する瞬間に呼び出す。
    // 古い session の sessionId を残すと query が再開して snackbar が再表示されるため、
    // sessionId をクリアすることで根本から無効化する。
    const clearNotAllocHint = useCallback(() => {
      setShowNotAllocSuggestionSnackbar(false);
      setNotAllocSessionId(null);
    }, []);

    // 自動配車後に「割り当たらなかった案件 (notAllocIds)」を選択状態へ復元するための pending state。
    // 直接 setSelectedUnassignedIds で入れると、別 useEffect で「unassigned に存在しない id を弾く」
    // フィルタが先に走って消えるタイミングがあるため、この pending を経由して
    // unassigned に notAllocIds が反映されたフレームで一括適用する（反映側の effect は後方に定義）。
    const [pendingNotAllocSelect, setPendingNotAllocSelect] = useState<number[] | null>(null);

    // override 2 つをクリアする。queued 完了時は controller の shouldClearLocalOverrides
    // 経由で、immediate/skipped 完了時と失敗時は画面側の hooks から直接呼ぶ
    const clearLocalOverrides = useCallback(() => {
      setOverrideRowCards(new Map());
      setOverrideUnassigned(null);
    }, []);

    // カード配車の 8 hooks（設計書「各画面の実装担当」）。controller は「撃って応答を反映する」
    // までを持ち、override・未割当ヒント・通知は画面固有の状態としてここで持つ
    const autoDispatchHooks: AutoDispatchHooks = {
      onRunAccepted: () => {
        // ここからは現在の override を新しい job の入力として扱う。
        // 以降に追加操作が入った場合は override 監視 effect が再度 true にする。
        hasQueuedOverrideChangeRef.current = false;
      },
      onStarted: ({ mode, orderIds }) => {
        if (mode === 'transfer') {
          enqueueSnackbar(`${orderIds.length.toLocaleString()}件の配送計画を移動します`);
        } else if (mode === 'swap') {
          // 入れ替えは対象を deliveryId で指定するため件数を出せない。「自動配車を実行中」も
          // 実際の操作と違うので /map と同じ専用の文言にする
          enqueueSnackbar('配送計画を入れ替えます');
        } else {
          enqueueSnackbar('自動配車を実行中...');
        }
        clearNotAllocHint();
        if (mode === 'normal' && orderIds.length > 0) {
          // 実行開始時点で未割当リストから対象案件を即時削除（V3Plans と同じオプティミスティック更新）。
          // polling 完了後にキャッシュが再取得され、useEffect で override がリセットされる。
          const removeIds = new Set(orderIds.map((id) => `lr-${id}`));
          setOverrideUnassigned((prev) => {
            const before = prev ?? serverUnassigned;
            // 停止したときに戻せるよう、消す前の一覧を覚えておく（override 全体を捨てると
            // D&D で動かした並びまで戻ってしまう）
            unassignedBeforeRunRef.current = before;
            return before.filter((u) => !removeIds.has(u.id));
          });
        }
      },
      shouldClearLocalOverrides: () => shouldClearOverrideAfterPlanningResult(hasQueuedOverrideChangeRef.current),
      clearLocalOverrides,
      onCompleted: (result, { mode, completionKind }) => {
        const effects = resolveCompletionEffects(mode, completionKind);
        // 結果が出たので「止めたら戻す」ための控えは捨てる。割り付いた案件の選択は
        // このあとの整理で落ちてよい
        unassignedBeforeRunRef.current = null;

        if (effects.clearOverride) {
          hasQueuedOverrideChangeRef.current = false;
          clearLocalOverrides();
        }

        const hasNotAlloc = effects.updateNotAllocSession
          && result.notAllocatedOrderIds.length > 0 && result.sessionId != null;

        if (effects.updateNotAllocSession) {
          setNotAllocSessionId(hasNotAlloc ? result.sessionId : null);
        }

        if (effects.restoreNotAllocSelection) {
          // unassigned 側のキャッシュ反映と React 描画タイミングの差で直接 set すると
          // 別 effect のフィルタに弾かれることがあるため、pending → 揃ってから確定の二段構え
          setPendingNotAllocSelect(result.notAllocatedOrderIds);
        }

        if (effects.notify) {
          if (hasNotAlloc) {
            enqueueSnackbar(
              `${result.notAllocatedOrderIds.length.toLocaleString()}件が割り当たりませんでした。割当たらない理由を確認してください。`,
            );
          } else {
            enqueueSnackbar('自動配車が完了しました', { variant: 'success' });
          }
        }
      },
      onScopeChanged: () => {
        resetOverrideForScopeChange({ setOverrideRowCards, setOverrideUnassigned, hasQueuedOverrideChangeRef });
      },
      /**
       * 利用者が停止したとき。1 件も割り付いていないので、実行開始時に消した未割当を戻す。
       * 選択には触らない（戻ってきた案件をそのまま選び直せるようにする）。
       *
       * Why not clearLocalOverrides: cancel は override を保持する契約（D&D で動かした
       * 位置を残す）。未割当だけを消す前の状態へ戻す。
       */
      onCancelled: () => {
        const before = unassignedBeforeRunRef.current;
        unassignedBeforeRunRef.current = null;
        if (before) setOverrideUnassigned(before);
        clearNotAllocHint();
      },
      onFailed: (error, { mode, phase }) => {
        hasQueuedOverrideChangeRef.current = false;
        unassignedBeforeRunRef.current = null;
        clearLocalOverrides();

        // console.error のタグは phase ごとに元実装のまま残す（文言分岐だけを
        // formatAutoDispatchFailure へ抽出。Why not タグも抽出: 画面ごとのログ識別子で
        // あり、通知文言とは別の関心事）
        const errorTag = phase === 'reset'
          ? 'operationBulkResetting 失敗'
          : (phase === 'polling' ? 'polling エラー' : 'mutation エラー');
        console.error(`[convenience-dispatch] ${errorTag}`, error);
        enqueueSnackbar(formatAutoDispatchFailure(error, { mode, phase }), { variant: 'error' });
      },
      // scenario は使わない
    };

    const controller = useAutoDispatchController({
      scope: { startOn, endOn, companyId },
      hooks: autoDispatchHooks,
      deps: {
        // normal/transfer/timeline/operationBulkResetting は 1 箇所の useMutationPlanningV5()
        // から共有する。controller が独自インスタンスを持つと、operationBulkResetting.isPending
        // は本番では別インスタンスの pending を反映しなくなり（useMutation は呼び出しごとに
        // 独立した observer を作るため）、手動リセット (:624)・運賃計算の isResetting 判定・
        // 進行中トーストの文言分岐が本番でだけ壊れる（特性テストは useMutationPlanningV5 自体を
        // モックするため、この食い違いを検出できない）
        usePlanningMutations: () => ({
          normalPlanning, transferPlanning, timelinePlanning, swapPlanning, operationBulkResetting,
        }),
      },
    });

    // 実行中フラグ = mutation 中 or polling 中（controller が一元管理する）
    // operationBulkResetting.isPending を内包する契約
    // （resolveConvenienceTariffAvailability.ts の isAutoDispatching コメント参照）。
    // controller.isBusy だけでは手動リセット中（詳細パネルの「未割当に戻す」等）を
    // 拾えない（whole-branch レビュー I-1）
    const isAutoDispatching = controller.isBusy || operationBulkResetting.isPending;

    /**
     * 確認ダイアログの `onConfirm` は「開いた時点のクロージャ」として state に入るので、
     * busy 判定は ref 越しに押した時点の最新値を読む。render 時の値を捕まえると
     * 「開く → 実行開始 → 確定」でガードが素通りする
     * (resolveBusyGuardReason.ts の GetBusyGuardReason の Why)。
     *
     * 同期は render 中の代入ではなく useLayoutEffect で行う（render を副作用なしに保つ）。
     * 同じ理由で useUnassignedOverride / useUnassignedRemovalOptimistic もこの形にしている。
     * ref を読むのはクリック時（= コミット後）だけなので、代入の遅れは判定に影響しない。
     */
    const busyStateRef = useRef({ isBusy: false, isResetting: false });
    useLayoutEffect(() => {
      busyStateRef.current = { isBusy: controller.isBusy, isResetting: operationBulkResetting.isPending };
    }, [controller.isBusy, operationBulkResetting.isPending]);
    const getBusyGuardReason = useCallback(() => resolveBusyGuardReason(busyStateRef.current), []);

    /**
     * 選択中の 2 車両の配送計画を入れ替える（/map にしか無かった機能。log_web_issues#297）。
     * ボタンを出す条件 (resolveSwapTargets) は共有の車両リストが持ち、断る条件と文言は
     * resolveSwapBlockReason に閉じ込めて /map・/plans と揃える。同じ操作が画面ごとに
     * 通ったり通らなかったりすると、どちらが正しいのか利用者に判断できない。
     *
     * 実行は controller.runSwap に任せる。入れ替えも queued で返って非同期に計算されるため、
     * 直接 mutate するとポーリングに乗らず、結果が反映されないまま完了扱いになる。
     */
    const runSwap = controller.runSwap;
    const handleSwapSelected = useCallback((fromDeliveryId: number, toDeliveryId: number) => {
      const deliveryIds: [number, number] = [fromDeliveryId, toDeliveryId];
      const blockReason = resolveSwapBlockReason(deliveryIds, enrichedDeliveries);
      if (blockReason) { enqueueSnackbar(blockReason, { variant: 'error' }); return; }

      setConfirmDialog({
        title: '配送計画を入れ替えますか？',
        description: '選択した2車両の配送計画を入れ替えます。',
        onConfirm: () => {
          // 自動配車の実行中に割当を動かすと、計算結果と画面が食い違う（配車リセットと同じガード）
          const busyReason = getBusyGuardReason();
          if (busyReason) { enqueueSnackbar(busyReason, { variant: 'error' }); return; }
          // 入れ替えた後も選ばれたままだと、続けて押して元に戻したように見える
          setSelectedRowIds(new Set());
          runSwap({ deliveryIds });
        },
      });
      // controller の戻り値は毎レンダー新しいオブジェクト。runSwap は useCallback で
      // 安定しているので、そちらを依存にする
    }, [enrichedDeliveries, enqueueSnackbar, getBusyGuardReason, runSwap]);

    // ─────────────────────────────────────────────────────────────
    // 配車サマリの運賃計算。可否の判定は resolveConvenienceTariffAvailability に
    // 閉じ込め、ここでは呼ぶだけにする (同じ規則が /map 側と割れないようにするため)。
    // ─────────────────────────────────────────────────────────────
    const [tariffDialogOpen, setTariffDialogOpen] = useState(false);
    const tariffAction = useTariffCalculationAction();
    const { targetIds: tariffTargetIds, disabledReason: tariffDisabledReason } = useMemo(
      () => resolveConvenienceTariffAvailability({
        fixedRouteId,
        deliveries,
        allDeliveries: allDeliveriesQuery.data ?? [],
        deliveriesQueryState: {
          isError: deliveriesQuery.isError,
          isFetching: deliveriesQuery.isFetching,
          hasNextPage: Boolean(deliveriesQuery.hasNextPage),
        },
        allDeliveriesQueryState: {
          isError: allDeliveriesQuery.isError,
          isFetching: allDeliveriesQuery.isFetching,
        },
        hasOverride: overrideRowCards.size > 0 || overrideUnassigned !== null,
        isAutoDispatching,
        isResetting: operationBulkResetting.isPending,
        // 実行中に押し直せると同じ計算が二重に走る
        isCalculatingTariff: tariffAction.isExecuting,
        isOrderAttrFilterActive: isTariffBlockedByOrderAttrFilter,
      }),
      [fixedRouteId, deliveries, allDeliveriesQuery, deliveriesQuery, overrideRowCards, overrideUnassigned,
        isAutoDispatching, operationBulkResetting.isPending, isTariffBlockedByOrderAttrFilter],
    );

    // applyResetToCaches は useRotationEditing への注入（:631 相当）のため前段へ移動済み。

    // 車両 ON/OFF
    //   - OFF: shift_truck/shift ごと削除（紐づく leg_run の operations もリセットされる）
    //   - ON: 単純に UI 状態を ON に戻す（API 呼び出しなし）
    // ※ Shift/ShiftTruck まで消すのでスイッチ自体の意味は薄れるが、UI は別途調整予定

    const refetchAfterEdit = useCallback(async () => {
      // useInfiniteQuery (staleTime: Infinity) のため明示 refetch
      await Promise.all([
        deliveriesQuery.refetch(),
        legRunsQuery.refetch(),
        allDeliveriesQuery.refetch(),
      ]);
    }, [deliveriesQuery, legRunsQuery, allDeliveriesQuery]);

    // 車両 / 車番 / ドライバーの差し替えは /map と共有する（順序を画面ごとに書くとずれる）
    const {
      handlers: vehicleAssignmentHandlers,
      menuProps: vehicleAssignmentMenuProps,
    } = useVehicleAssignmentEditing({
      rowGroups,
      deliveries,
      trucks,
      drivers,
      refetchAfterEdit,
      notify: (message, variant) => enqueueSnackbar(message, { variant }),
      setVehicleOverride,
      clearVehicleOverride,
    });

    // 「計画無」バッジの × 。以前は稼働 ON/OFF の Switch で ShiftTruck だけ消していたが、
    // 可逆トグルに見える見た目と片道操作の実態が食い違っていた。/map と同じく勤務ごと消す
    //
    // notify は前段（:621）の安定化済みのものを渡す。confirm は setConfirmDialog を直接渡す
    // （setState 関数は安定。`(params) => setConfirmDialog(params)` のようにラップすると、その
    // ラップ自体が毎レンダー新しい関数になり requestDeleteShift が作り直されてしまう）。
    const { requestDeleteShift, deletingShiftTruckIds } = useShiftDeletion({
      deliveries,
      refetchAfterDelete: refetchAfterEdit,
      notify,
      confirm: setConfirmDialog,
    });

    // 削除中の勤務を画面から先に消す。失敗すれば集合から外れて戻る（/map と同じ）
    const visibleRowGroups = useMemo(
      () => hideDeletingShifts(filteredRowGroups, deletingShiftTruckIds),
      [filteredRowGroups, deletingShiftTruckIds],
    );

    /**
     * 消えた行の選択を外す。/map と同じ理由（「n件選択中」が減らないと、画面に無い
     * 車両が件数にも自動配車の対象にも残る）。
     *
     * 判定は絞り込み前の集合から作る。表示条件で隠れているだけの行まで外すと、
     * 選び直す手段ごと消える。取得中は見ない（一瞬空になったときに選択を捨てない）。
     */
    const existingRowIds = useMemo(
      () => new Set(
        hideDeletingShifts(applyHiddenFilter(rowGroups), deletingShiftTruckIds)
          .flatMap((g) => g.rows.map((r) => r.rowId)),
      ),
      [rowGroups, applyHiddenFilter, deletingShiftTruckIds],
    );
    useEffect(() => {
      if (deliveriesQuery.isLoading) return;
      setSelectedRowIds((prev) => pruneSelectedRowIds(prev, existingRowIds));
    }, [existingRowIds, deliveriesQuery.isLoading]);

    // 選択中の行 (= 配送計画) をまとめて削除する。
    // Why not 選択解除: 戻したあと続けて同じ車両へ配車し直す使い方をするため、選択は残す
    // （既存のマップ画面・/map と揃える）。行ごと消えたときは pruneSelectedRowIds が落とす。
    // ・確認時には 「n件の配送計画を削除します」 (n = 選択行数)
    // ・完了時には 「m件の配送計画を削除しました」
    //   m は実際に削除対象になった行数 (= legRun (案件) を持つ行のみ)。
    //   3 行選択していて 1 行に案件が無い場合 → n=3, m=2 となる。
    const handleUnassignSelected = useCallback(() => {
      if (selectedRowIds.size === 0) return;

      const selectedCount = selectedRowIds.size;
      // 対象の集め方は /map と共有する（片方だけ直すと削除件数が食い違う）
      const { legRunIds: ids, deletableRowCount } = collectResetTargets(selectedRowIds, serverRowGroups);

      const exec = async () => {
        let apiSucceeded = false;
        if (ids.length > 0) {
          try {
            await operationBulkResettingMutateAsync(ids);
            apiSucceeded = true;
          } catch (e) {
            const err = e as AxiosError<{ message: string }>;
            enqueueSnackbar(err.response?.data?.message ?? '処理に失敗しました', { variant: 'error' });
          }
        } else {
          // 削除対象 (案件付き行) が無くても結果表示は行う
          apiSucceeded = true;
        }
        if (apiSucceeded) {
          if (ids.length > 0) applyResetToCaches(ids);
          enqueueSnackbar(`${deletableRowCount}件の配送計画を削除しました`, { variant: 'success' });
        }
      };

      setConfirmDialog({
        title: '配送計画を削除しますか？',
        description: `${selectedCount}件の配送計画を削除します。`,
        onConfirm: exec,
      });
      // 依存は operationBulkResettingMutateAsync（mutateAsync のみ、:626 相当で切り出し済み）。
      // 理由は handleTruckOrderChange の mutate 絞り込みと同じ（useMutation の戻り値オブジェクトは
      // 毎レンダー新規になり、丸ごと依存にすると左のトラックリストに渡る onUnassignSelected が
      // 毎回作り直され memo が破れる）
    }, [selectedRowIds, serverRowGroups, operationBulkResettingMutateAsync, enqueueSnackbar, applyResetToCaches]);

    void rowIdFromTruck;

    // ─────────────────────────────────────────────────────────────
    // 自動配車（操作アイドル検知 + debounce 3秒 + timelinePlanning）
    // ─────────────────────────────────────────────────────────────
    // rowId → shift_truck_id (= delivery.id) マップ。サーバ既存の delivery を対象。
    const rowIdToShiftTruckId = useMemo(() => buildRowIdToShiftTruckId(deliveries), [deliveries]);

    // truckId → delivery.id: ローカルで追加した仮想回転 (extraRotations) 用に
    // そのトラックの代表 delivery を引けるようにする。
    const truckIdToDeliveryId = useMemo(() => {
      const m = new Map<number, number>();
      deliveries.forEach((d) => {
        if (!m.has(d.truckId)) m.set(d.truckId, d.id);
      });
      return m;
    }, [deliveries]);

    const runAutoDispatch = useCallback(async () => {
      // 仮想回転(新規追加) は shift_truck_id 未確定を表す marker
      const VIRTUAL_STID = -1;

      if (controller.isBusy) {
        console.log('[convenience-dispatch] runAutoDispatch skip: 実行中');
        return;
      }
      if (overrideRowCards.size === 0 && overrideUnassigned === null) {
        console.log('[convenience-dispatch] runAutoDispatch skip: override 空');
        return;
      }
      // hasQueuedOverrideChangeRef のリセットは controller.runTimeline 内の
      // onRunAccepted hook が行う（現在の override を新しい job の入力として扱う）
      console.log('[convenience-dispatch] runAutoDispatch start', {
        overrideRows: overrideRowCards.size,
        overrideUnassigned: overrideUnassigned?.length ?? null,
      });

      // legRun の所在は (stid, cycle) のペアで表す。
      // - stid: サーバ既存 delivery の id / VIRTUAL_STID (-1) / null (未割当)
      // - cycle: 回転番号（未割当時は null）
      // cycle 情報を含めることで、同一 truck 内での回転移動も検知できる。
      type LegRunLocation = { stid: number | null; cycle: number | null };

      // 元の legRun → 所在マップ（既存 operation のある legRun のみ）
      const originalLocation = new Map<number, LegRunLocation>();
      deliveries.forEach((d) => {
        d.operations.forEach((op) => {
          const lrId = op.shipmentOrderLegRunId;
          // 同じ legRun の op が複数ある場合、最初の cycle を採用（全て同じ cycle であるはず）
          if (!originalLocation.has(lrId)) {
            originalLocation.set(lrId, { stid: d.id, cycle: op.cycle >= 1 ? op.cycle : 1 });
          }
        });
      });

      // 現在（override 反映後）の legRun → 所在マップ
      const currentLocation = new Map<number, LegRunLocation>();
      const currentUnassigned = overrideUnassigned ?? serverUnassigned;
      parseUnassignedIds(currentUnassigned.map((u) => u.id)).forEach((lrId) => {
        currentLocation.set(lrId, { stid: null, cycle: null });
      });
      rowGroups.forEach((g) => g.rows.forEach((r) => {
        // 仮想回転 (回転追加) は対応する delivery.id が rowIdToShiftTruckId に無いが、
        // 同じ truckId に既存 delivery があればそこへ new cycle として寄せる
        const stid = rowIdToShiftTruckId.get(r.rowId)
          ?? truckIdToDeliveryId.get(r.truckId)
          ?? VIRTUAL_STID;
        const cycle = r.rotationNumber;
        r.cards.forEach((c) => {
          // 所在判定には delivery カード（降地）のみを用いる。
          // loading カードにも同じ legRunId が含まれているため、両方を見ると別トラックへの
          // 移動時に「移動元の loading」と「移動先の delivery」で currentLocation が上書き合戦になる。
          if (c.type !== 'delivery') return;
          // no-delivery / fr-template は「まだ割り当てられていない」状態なので current location 対象外
          if (c.status === 'no-delivery' || c.status === 'fr-template') return;
          (c.legRunIds ?? []).forEach((lrId) => {
            currentLocation.set(lrId, { stid, cycle });
          });
        });
      }));

      // 変更があった legRun を特定（stid または cycle が変わったもの）
      const allLegRunIds = new Set<number>();
      originalLocation.forEach((_, id) => allLegRunIds.add(id));
      currentLocation.forEach((_, id) => allLegRunIds.add(id));
      const changedLegRunIds = new Set<number>();
      allLegRunIds.forEach((lrId) => {
        const orig = originalLocation.get(lrId);
        const now = currentLocation.get(lrId);
        const origStid = orig?.stid ?? null;
        const nowStid = now?.stid ?? null;
        const origCycle = orig?.cycle ?? null;
        const nowCycle = now?.cycle ?? null;
        if (origStid !== nowStid || origCycle !== nowCycle) changedLegRunIds.add(lrId);
      });

      // 同一 (stid, cycle) 内での順序変更も検知する。stid / cycle 変化が無くても、
      // override の表示順が server の operations の順序と異なれば「順序変更あり」として
      // その row 内の全 legRun を changed 扱いにする (timelinePlanning に並び順を伝える)。
      rowGroups.forEach((g) => g.rows.forEach((r) => {
        const stid = rowIdToShiftTruckId.get(r.rowId) ?? truckIdToDeliveryId.get(r.truckId) ?? VIRTUAL_STID;
        if (stid === VIRTUAL_STID) return;
        const cycle = r.rotationNumber;
        const currentOrder: number[] = [];
        r.cards.forEach((c) => {
          if (c.type !== 'delivery') return;
          if (c.status === 'no-delivery' || c.status === 'fr-template') return;
          (c.legRunIds ?? []).forEach((lrId) => {
            if (!currentOrder.includes(lrId)) currentOrder.push(lrId);
          });
        });
        if (currentOrder.length === 0) return;
        // server 側: 同じ (stid, cycle) に属する 降 op を arrival_at 順で並べた legRun id 列
        const delivery = deliveries.find((d) => d.id === stid);
        if (!delivery) return;
        const serverOrder: number[] = delivery.operations
          .filter((op) => op.action === '降' && (op.cycle >= 1 ? op.cycle : 1) === cycle)
          .slice()
          .sort((a, b) => new Date(a.arrivalAt).getTime() - new Date(b.arrivalAt).getTime())
          .map((op) => op.shipmentOrderLegRunId)
          .filter((id, idx, arr) => arr.indexOf(id) === idx);
        if (serverOrder.length === 0) return;
        const sameOrder = currentOrder.length === serverOrder.length
          && currentOrder.every((id, i) => id === serverOrder[i]);
        if (!sameOrder) {
          currentOrder.forEach((id) => changedLegRunIds.add(id));
        }
      }));

      console.log('[convenience-dispatch] changedLegRunIds', Array.from(changedLegRunIds));
      if (changedLegRunIds.size === 0) {
        console.log('[convenience-dispatch] runAutoDispatch skip: 変更 legRun 無し');
        return;
      }

      // 「row → unassigned」(配車済み → 未割当に戻す) ドロップを抽出する。
      // 実際の reset 実行・isUnassignOnly 判定・その後の分岐は buildTimelineRunInput +
      // controller.runTimeline が持つ（Task 8 で移設。「未割当に戻す」ボタンと同等の挙動）
      const unassignedDropIds: number[] = [];
      changedLegRunIds.forEach((lrId) => {
        const orig = originalLocation.get(lrId);
        const now = currentLocation.get(lrId);
        // 元は配車済 (stid > 0) → 現在は unassigned (stid == null)
        if (orig?.stid != null && orig.stid > 0 && (now == null || now.stid == null)) {
          unassignedDropIds.push(lrId);
        }
      });

      await controller.runTimeline(buildTimelineRunInput({
        changedLegRunIds,
        unassignedDropIds,
        buildBody: (remaining) => {
          // 仮想回転に割当てられた legRun は timeline に送れない（backend delivery 未確定）
          const virtualChangedIds = Array.from(remaining).filter(
            (lrId) => currentLocation.get(lrId)?.stid === VIRTUAL_STID,
          );
          if (virtualChangedIds.length > 0) {
            console.warn('[convenience-dispatch] 仮想回転の割当は backend に反映不可（spec調整要）', virtualChangedIds);
          }

          // 影響を受けた delivery (stid) の集合: 変更 legRun の 移動元 / 移動先 を union
          // 影響を受けた delivery については (cycle 順 → 積→降 順) で全 ops を再構築する
          const affectedStids = new Set<number>();
          remaining.forEach((lrId) => {
            const orig = originalLocation.get(lrId);
            const current = currentLocation.get(lrId);
            if (orig?.stid != null && orig.stid > 0) affectedStids.add(orig.stid);
            if (current?.stid != null && current.stid > 0) affectedStids.add(current.stid);
          });

          // 既存 operation の (lrId, action) → operation id 索引。リクエストの id 引き継ぎに使う
          const existingOpKeyOf = (lrId: number, action: string) => `${lrId}-${action}`;
          const existingOpIdIndex = new Map<string, number>();
          deliveries.forEach((d) => {
            d.operations.forEach((op) => {
              existingOpIdIndex.set(existingOpKeyOf(op.shipmentOrderLegRunId, op.action), op.id);
            });
          });
          const existingOperationIdOf = (lrId: number, action: string) => (
            existingOpIdIndex.get(existingOpKeyOf(lrId, action))
          );

          // timeline_operations 構築
          // 仕様: 影響 delivery については (stid, cycle) 単位で表示順に並べ、各グループ内で 積→降 を
          //       時系列が単調増加するように再生成する。例:
          //       移動前 cycle1=[A積,B積,C積,A降,B降,C降]
          //       A を cycle2 にドロップ後の display: cycle1=[B,C], cycle2=[A]
          //       → request: [B積, C積, B降, C降, A積, A降] (cycle1 全て → cycle2 全て、時刻も単調)
          const fallbackStart = new Date(`${startOn}T00:00:00+09:00`);
          const lastDepartureByStid = new Map<number, Date>();
          const timelineEntities: TimelineRequestEntity[] = [];

          rowGroups.forEach((g) => g.rows.forEach((r) => {
            // 仮想回転は truckId の代表 delivery.id にフォールバック
            const stid = rowIdToShiftTruckId.get(r.rowId) ?? truckIdToDeliveryId.get(r.truckId);
            if (stid == null) return;
            if (!affectedStids.has(stid)) return;
            const cycle = r.rotationNumber;

            // 当該 row の delivery カード列から legRun を表示順で抽出 (重複除外)
            const seen = new Set<number>();
            const legRunsInOrder: ShipmentOrderLegRunForPlanEntity[] = [];
            r.cards.forEach((c) => {
              if (c.type !== 'delivery') return;
              if (c.status === 'no-delivery' || c.status === 'fr-template') return;
              (c.legRunIds ?? []).forEach((lrId) => {
                if (seen.has(lrId)) return;
                const lr = legRuns.find((x) => x.id === lrId);
                if (!lr) return;
                seen.add(lrId);
                legRunsInOrder.push(lr);
              });
            });
            if (legRunsInOrder.length === 0) return;

            // baseline: 同 stid の前 cycle の最終 departure + 30分 → なければ delivery 開始 → fallback
            const prior = lastDepartureByStid.get(stid);
            const delivery = deliveries.find((d) => d.id === stid);
            const baseline = prior
              ? addSecondsToDate(prior, TRAVEL_SECONDS_BETWEEN_PLACES)
              : (delivery ? new Date(delivery.startAt) : fallbackStart);

            const ops = computeOpsForCycle({ stid, legRuns: legRunsInOrder, cycle, baseline });
            if (ops.length === 0) return;

            // 次 cycle の baseline 用に最終 departure を track
            const last = ops.reduce(
              (max, o) => (o.departure_at.getTime() > max.getTime() ? o.departure_at : max),
              ops[0].departure_at,
            );
            lastDepartureByStid.set(stid, last);

            timelineEntities.push(...buildTimelineRequestEntities(ops, existingOperationIdOf));
          }));

          // 空になった delivery の id
          // - 元から operations が空の delivery は除外（シフトのみの配車枠）
          // - 元は legRun を持っていたが、現在 current Location に誰も居ない delivery だけ対象
          const originallyPopulatedDeliveryIds = new Set<number>();
          deliveries.forEach((d) => {
            if (d.operations.length > 0) originallyPopulatedDeliveryIds.add(d.id);
          });
          const deliveryIdsWithOps = new Set<number>();
          currentLocation.forEach((loc) => {
            if (loc.stid != null && loc.stid > 0) deliveryIdsWithOps.add(loc.stid);
          });
          const emptyDeliveryIds: number[] = [];
          originallyPopulatedDeliveryIds.forEach((id) => {
            if (!deliveryIdsWithOps.has(id)) emptyDeliveryIds.push(id);
          });

          console.log('[convenience-dispatch] request', {
            timelineEntities: timelineEntities.length,
            emptyDeliveryIds: emptyDeliveryIds.length,
            virtualChangedIds,
          });

          return { timelineEntities, emptyDeliveryIds };
        },
      }));
    }, [
      controller,
      overrideRowCards,
      overrideUnassigned,
      deliveries,
      legRuns,
      rowGroups,
      rowIdToShiftTruckId,
      truckIdToDeliveryId,
      serverUnassigned,
      startOn,
    ]);

    // 最新 runAutoDispatch を ref 経由で参照（debounce 関数から安定呼び出し）
    const runAutoDispatchRef = useRef(runAutoDispatch);
    useEffect(() => {
      runAutoDispatchRef.current = runAutoDispatch;
    }, [runAutoDispatch]);

    const debouncedAutoDispatch = useMemo(
      () => debounce(() => {
        console.log('[convenience-dispatch] debounce 発火 → runAutoDispatch 実行');
        void runAutoDispatchRef.current();
      }, 2000),
      [],
    );

    // override 変更を検知。実行中の自動配車があれば即キャンセルして新規 debounce を開始する。
    // 「直前の job は無駄なので捨て、最新状態で再計算する」というポリシー (Q2-A)。
    // 初回マウント時の override = 空 では発火させない (prev=null check)
    const prevOverrideRef = useRef<{ r: typeof overrideRowCards; u: typeof overrideUnassigned } | null>(null);
    // controller.isBusy が「直前のレンダーでも既に true だったか」。cancel 判定
    // （shouldCancelForOverrideChange）が「受付と同一 commit」の自己キャンセルを
    // 避けるために使う。詳細は同ファイルの Why コメント参照
    const wasBusyRef = useRef(false);
    useEffect(() => {
      const prev = prevOverrideRef.current;
      const overrideChanged = prev !== null
        && (prev.r !== overrideRowCards || prev.u !== overrideUnassigned);
      prevOverrideRef.current = { r: overrideRowCards, u: overrideUnassigned };

      const wasBusy = wasBusyRef.current;
      wasBusyRef.current = controller.isBusy;

      if (shouldCancelForOverrideChange({ overrideChanged, isBusy: controller.isBusy, wasBusy })) {
        hasQueuedOverrideChangeRef.current = true;
        console.log('[convenience-dispatch] running 中に override 変更 → queued + cancel');
        controller.cancel();
      }
    }, [overrideRowCards, overrideUnassigned, controller]);

    // dirty 状態になった瞬間に debounce を kick。以降のユーザー操作で延長される
    useEffect(() => {
      const isDirty = overrideRowCards.size > 0 || overrideUnassigned !== null;
      if (isDirty && !isAutoDispatching) {
        console.log('[convenience-dispatch] dirty 検知 → debounce 開始');
        debouncedAutoDispatch();
      } else {
        debouncedAutoDispatch.cancel();
      }
    }, [overrideRowCards, overrideUnassigned, isAutoDispatching, debouncedAutoDispatch]);

    // ユーザー操作中は debounce をリセットして自動発火を遅らせる
    useEffect(() => {
      const handler = () => {
        const isDirty = overrideRowCards.size > 0 || overrideUnassigned !== null;
        if (isDirty && !isAutoDispatching) {
          debouncedAutoDispatch();
        }
      };
      const eventTypes = ['mousedown', 'mousemove', 'mouseup', 'keydown', 'keyup', 'touchstart', 'touchmove', 'touchend'];
      eventTypes.forEach((t) => document.addEventListener(t, handler, { passive: true }));
      return () => {
        eventTypes.forEach((t) => document.removeEventListener(t, handler));
      };
    }, [debouncedAutoDispatch, overrideRowCards, overrideUnassigned, isAutoDispatching]);

    // unmount 時にキャンセル
    useEffect(() => () => { debouncedAutoDispatch.cancel(); }, [debouncedAutoDispatch]);

    // カード選択とドラッグ表示は Presenter の state ではなく BoardInteractionStore に
    // 持つ。カードを 1 枚選ぶ / ドロップ先が 1 つ動くだけで Presenter → Board → 全行 →
    // 全カードまで再描画されていたのを、変わったものだけが購読で描き直す形にした。
    //
    // store は Presenter 単位で 1 つだけ作る。module singleton にすると、ドラッグ中の
    // 画面遷移や同一 scope での再マウントで activeCard・タイマ・選択が残り、テスト間でも漏れる
    const boardStoreRef = useRef<BoardInteractionStore | null>(null);
    boardStoreRef.current ??= createBoardInteractionStore();
    const boardStore = boardStoreRef.current;

    // unmount で購読とタイマを畳む。残すと、消えた画面の store をハイライト解除タイマが
    // 書き換え続ける（flashRecentlyMoved がスケジュールした setTimeout はこの effect が
    // 無いと生き残る）。
    //
    // Why not StrictMode 安全な形にする: dispose() は不可逆（disposed フラグが立ち
    // subscribe が no-op を返す）ため、StrictMode の setup→cleanup→setup で store が
    // 死んだまま残り、dev だけ盤面の選択・ドラッグが黙って効かなくなる。二重呼び出しの
    // 間に再 render は入らないので、ref を null にしても captured な boardStore が
    // 使われて直らない。このアプリは StrictMode を使っていない（src/index.tsx）ので
    // 現状は踏まないが、導入するなら dispose を可逆にするか cleanup を外す必要がある。
    useEffect(() => () => { boardStore.dispose(); }, [boardStore]);

    // 会社・対象日が変わったらカード選択とドラッグ表示を捨てる。
    // 別の日のカード id が選択に残ると「n件選択中」だけが残って対象の無い状態になる。
    //
    // Why not 初回 mount をスキップするガード: reset() は commit(INITIAL_STATE) で、
    // store の初期 state も同じ INITIAL_STATE 参照。commit の `next === state` ガードが
    // 効くので、マウント時にこの effect が走っても listener は発火しない（余分な
    // 再描画は起きない）。ガードを足す意味が無い。
    useEffect(() => { boardStore.reset(); }, [planningRangeKey, boardStore]);

    const handleRestoreSplit = useCallback((item: UnassignedItem) => {
      const rawId = item.id;
      const legRunId = rawId.startsWith('lr-') ? parseInt(rawId.slice(3), 10) : NaN;
      if (Number.isNaN(legRunId)) return;
      setConfirmDialog({
        title: '分割した案件を復元しますか？',
        description: '分割した案件を元に戻します。よろしいですか？',
        onConfirm: () => {
          restoreSplitLegRunMutate(legRunId, {
            onSuccess: () => {
              enqueueSnackbar('分割した案件を元に戻しました', { variant: 'success' });
              void legRunsQueryRefetchForRestoreSplit();
            },
            onError: () => {
              enqueueSnackbar('分割した案件を元に戻す処理でエラーが発生しました', { variant: 'error' });
            },
          });
        },
      });
    }, [restoreSplitLegRunMutate, enqueueSnackbar, legRunsQueryRefetchForRestoreSplit, setConfirmDialog]);

    // フィルタで非表示になっている選択済み件数（自動配車ダイアログの警告に使う）
    const [hiddenSelectedCount, setHiddenSelectedCount] = useState(0);

    const [selectedUnassignedIds, setSelectedUnassignedIds] = useState<Set<string>>(new Set());
    const onSelectUnassigned = useCallback((id: string) => {
      setSelectedUnassignedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    }, []);

    // pendingNotAllocSelect state は前方（未割当ヒント state の近く）で宣言済み。
    // 自動配車などで割り当て済みになった案件は unassigned から消えるので、
    // 選択 state からも除外して「N件選択中」表記を実態に揃える。
    // pending がある間は (= 復元待ち) 厳密フィルタしない (応答到着後の一瞬で消えるのを防ぐ)。
    useEffect(() => {
      if (pendingNotAllocSelect != null) return;
      setSelectedUnassignedIds((prev) => {
        if (prev.size === 0) return prev;
        const validIds = new Set(unassigned.map((u) => u.id));
        // 自動配車に送って一時的に消している案件は、止めれば戻ってくるので選択を落とさない
        // （落とすと停止後に選び直しになる。利用者の指摘 2026/08/20）
        (unassignedBeforeRunRef.current ?? []).forEach((u) => validIds.add(u.id));
        let changed = false;
        const next = new Set<string>();
        prev.forEach((id) => {
          if (validIds.has(id)) next.add(id);
          else changed = true;
        });
        return changed ? next : prev;
      });
    }, [unassigned, pendingNotAllocSelect]);

    // pending な notAllocIds が unassigned 側に出揃ったら選択を確定する。
    useEffect(() => {
      if (pendingNotAllocSelect == null) return;
      // 全件割当成功 (notAllocIds 空) なら即時クリアして pending 解除
      if (pendingNotAllocSelect.length === 0) {
        setSelectedUnassignedIds(new Set());
        setPendingNotAllocSelect(null);
        return;
      }
      const targetIds = pendingNotAllocSelect.map((id) => `lr-${id}`);
      const validIds = new Set(unassigned.map((u) => u.id));
      // 期待する notAllocIds が全て unassigned に揃っていなければ次の更新を待つ
      if (!targetIds.every((id) => validIds.has(id))) return;
      setSelectedUnassignedIds(new Set(targetIds));
      setPendingNotAllocSelect(null);
    }, [pendingNotAllocSelect, unassigned]);

    // 中央ボードの全行が受け取る props なので、参照を据え置く。rowGroups の参照が
    // 変わるだけで新しい Set を流すと、1 台に回転を足しただけで全行の memo が破れる
    const keepRouteDeviationCardIds = useKeepSetIdentity<string>();
    const routeDeviationCardIds = useMemo<Set<string>>(() => {
      const s = new Set<string>();
      if (fixedRouteId == null) return keepRouteDeviationCardIds(s);
      // 並び替え後 (overrideRowCards 適用後) も判定が崩れないよう、source 側の
      // 「固定ルート外」フラグから legRunId 集合を作り、現在表示中の DispatchCard の
      // legRunIds と突き合わせる。index ベースの対応付けは D&D で順序が変わると壊れるため使わない。
      const deviationLegRunIds = new Set<number>();
      rowGroups.forEach((g) => g.rows.forEach((r) => {
        r.source.cards.forEach((srcCard) => {
          if (srcCard.kind !== 'unloading') return;
          if (!srcCard.isRouteDeviation) return;
          srcCard.legRuns.forEach((lr) => {
            if (lr.id != null) deviationLegRunIds.add(lr.id);
          });
        });
      }));
      if (deviationLegRunIds.size === 0) return keepRouteDeviationCardIds(s);

      rowGroups.forEach((g) => g.rows.forEach((r) => {
        r.cards.forEach((c) => {
          if (c.type !== 'delivery') return;
          if (c.status === 'no-delivery' || c.status === 'fr-template') return;
          const ids = c.legRunIds ?? [];
          if (ids.some((id) => deviationLegRunIds.has(id))) {
            s.add(c.id);
          }
        });
      }));
      return keepRouteDeviationCardIds(s);
    }, [rowGroups, fixedRouteId, keepRouteDeviationCardIds]);

    // D&D 設定
    const sensors = useSensors(useSensor(PointerSensor, boardPointerSensorOptions));
    const collisionDetection: CollisionDetection = useCallback((args) => {
      const pc = pointerWithin(args);
      if (pc.length > 0) {
        const cardCollision = pc.find((c) => !String(c.id).startsWith('vehicle-row-') && c.id !== 'unassigned');
        return cardCollision ? [cardCollision] : pc;
      }
      return rectIntersection(args);
    }, []);

    // ドラッグ表示の 4 状態 (activeCard / dragCount / dropPreview / recentlyMovedCardIds)
    // は boardStore が持つ。dropPreview は handleDragOver から更新されるので、
    // Presenter の state に置くとホバー先が変わるたびに Presenter 全体が再描画され、
    // そこから全カラムへ波及していた。今は購読している行 (VehicleRowCards)・カード
    // (DispatchCardView)・DragOverlay の中身 (DragOverlayCard) だけが描き直される。
    //
    // 直近のドロップで動いたカードのハイライトも store が持つ。フェードアウトのタイマも
    // store 側に一本化した (boardInteractionStore.ts の RECENTLY_MOVED_DURATION_MS)。
    // 発火するのは handleDragEnd の 3 経路 — 未割当→行 / 同 row 内の並び替え / 行→他行
    // (未割当→行・行→他行 は fr-template のリプレイスとマージを含む)。
    // 行→未割当 だけは対象外 (移動先がカードとして残らない)。

    // 現在ドラッグ中のソース所属 rowId (同 row 内の場合はプレビュー表示しない)
    const dragSourceRowIdRef = useRef<string | null>(null);

    // cardId → {rowId, index} の lookup
    const cardLookup = useMemo(() => {
      const map = new Map<string, { rowId: string; index: number }>();
      rowGroups.forEach((g) => g.rows.forEach((r) => {
        r.cards.forEach((c, idx) => {
          map.set(c.id, { rowId: r.rowId, index: idx });
        });
      }));
      return map;
    }, [rowGroups]);

    const rowLookup = useMemo(() => buildRowLookup(rowGroups), [rowGroups]);

    const handleDragStart = useCallback((event: DragStartEvent) => {
      const activeId = String(event.active.id);
      // 車両側カード? (複数ドラッグ非対応)
      const loc = cardLookup.get(activeId);
      if (loc) {
        const row = rowLookup.get(loc.rowId);
        if (row) {
          // boardInteractionStore の startDrag は dropPreview も null に戻すので、
          // 前のドラッグの残りを別途消す必要は無い
          boardStore.startDrag(row.cards[loc.index], 0);
          dragSourceRowIdRef.current = row.rowId;
          return;
        }
      }
      // 未割当?: 選択中アイテムなら一括ドラッグ件数を出す
      const u = unassigned.find((i) => i.id === activeId);
      if (u) {
        const isMulti = selectedUnassignedIds.has(activeId) && selectedUnassignedIds.size > 1;
        const card: DeliveryCard = {
          id: u.id,
          type: 'delivery',
          storeName: u.storeName,
          storeAddress: u.storeAddress,
          cases: u.cases,
          todayCases: u.cases,
          status: 'matched',
          isActive: true,
          origin: u.origin,
          destination: u.destination,
          originTimeWindow: u.originTimeWindow,
          destTimeWindow: u.destTimeWindow,
          shippers: u.shippers,
        };
        // 件数バッジは複数選択のときだけ出す (1 件のドラッグは 0 を渡す)
        boardStore.startDrag(card, isMulti ? selectedUnassignedIds.size : 0);
        dragSourceRowIdRef.current = null; // 未割当からなので源 row 無し
        return;
      }
      // どちらにも当たらなかったとき。startDrag を通らないので、前のドラッグの
      // プレビューが残る（startDrag は dropPreview も null に戻すが、ここは通らない）。
      //
      // Why not 到達不能として省く: dnd-kit が active にするのは登録済みの draggable だけで、
      // 登録しているのは中央カード (cardLookup) と未割当アイテムのラッパだけなので実際には
      // 到達しない。ただし store へ移す前は無条件に消していたので、その差を残さない
      boardStore.setDropPreview(null);
    }, [boardStore, cardLookup, rowLookup, unassigned, selectedUnassignedIds]);

    // ドラッグ中: 行 / カード上にホバーしたタイミングで「ここに入る」プレビュー位置を更新する。
    // 同 row 内の並び替えは SortableContext がカードをスライドして可視化するのでプレビュー不要。
    const handleDragOver = useCallback((event: DragOverEvent) => {
      const overId = event.over ? String(event.over.id) : null;
      if (!overId) {
        boardStore.setDropPreview(null);
        return;
      }
      // -nodrag サフィックス (loading / no-delivery カード) は剥がして lookup
      const lookupId = overId.endsWith('-nodrag') ? overId.slice(0, -'-nodrag'.length) : overId;
      const targetRowId = overId.startsWith('vehicle-row-') ? overId : cardLookup.get(lookupId)?.rowId;
      if (!targetRowId) {
        boardStore.setDropPreview(null);
        return;
      }
      // 同 row 内ドラッグ中はプレビュー非表示 (SortableContext が並び替え視覚化する)
      if (dragSourceRowIdRef.current && dragSourceRowIdRef.current === targetRowId) {
        boardStore.setDropPreview(null);
        return;
      }
      const targetRow = rowLookup.get(targetRowId);
      if (!targetRow) {
        boardStore.setDropPreview(null);
        return;
      }
      const overLoc = cardLookup.get(lookupId);
      const insertIndex = overLoc ? overLoc.index : targetRow.cards.length;
      boardStore.setDropPreview({ rowId: targetRowId, index: insertIndex });
    }, [boardStore, cardLookup, rowLookup]);

    const handleDragCancel = useCallback(() => {
      // endDrag は dropPreview だけでなく activeCard / dragCount も戻す
      // (boardInteractionStore の endDrag は dragCancel からの呼び出しを前提にしている)。
      // cancel でも dnd-kit は直前のオーバーレイを clone して 250ms 描き続けるので、
      // その間に見えるカードは DragOverlayCard が覚えている直前の値である
      // (理由は DragOverlayCard.tsx の lastCardRef のコメント)
      boardStore.endDrag();
      dragSourceRowIdRef.current = null;
    }, [boardStore]);

    const mutateRowCards = useCallback((rowId: string, updater: (cards: DispatchCard[]) => DispatchCard[]) => {
      setOverrideRowCards((prev) => {
        const base = prev.get(rowId) ?? rowLookup.get(rowId)?.cards ?? [];
        const next = new Map(prev);
        next.set(rowId, updater(base));
        return next;
      });
    }, [rowLookup]);

    const handleDragEnd = useCallback((event: DragEndEvent) => {
      const activeId = String(event.active.id);
      const overId = event.over ? String(event.over.id) : null;
      boardStore.endDrag();
      dragSourceRowIdRef.current = null;
      if (!overId) return;

      // 1) 未割当 → 車両row (loading/unloading を同地点マージ)
      const fromUnassigned = unassigned.find((u) => u.id === activeId);
      if (fromUnassigned) {
        // loading / no-delivery カードの sortable id は `${card.id}-nodrag` なので、
        // ドロップ先がそれらの場合は -nodrag を剥がして cardLookup で位置を引く。
        const lookupId = overId.endsWith('-nodrag') ? overId.slice(0, -'-nodrag'.length) : overId;
        const targetRowId = overId.startsWith('vehicle-row-') ? overId : cardLookup.get(lookupId)?.rowId;
        if (!targetRowId) return;
        const targetRow = rowLookup.get(targetRowId);
        if (!targetRow) return;

        const overLoc = cardLookup.get(lookupId);

        // 選択中アイテムが複数ある場合は一括移動 (サンプル準拠)
        const isMulti = selectedUnassignedIds.has(activeId) && selectedUnassignedIds.size > 1;
        const idsToMove = isMulti ? Array.from(selectedUnassignedIds) : [activeId];

        const legRunsToMove: ShipmentOrderLegRunForPlanEntity[] = [];
        for (const id of idsToMove) {
          const m = id.startsWith('lr-') ? parseInt(id.slice(3), 10) : NaN;
          const lr = legRuns.find((r) => r.id === m);
          if (lr) legRunsToMove.push(lr);
        }
        if (legRunsToMove.length === 0) return;

        const insertAt = overLoc ? overLoc.index : targetRow.cards.length;

        // setState の updater 内で副作用収集すると React のタイミング (strict mode の二重呼び出し
        // / 遅延適用 等) に依存して移動先 id を取り逃すことがあるため、配置結果は state 設定の
        // 「外側」で計算してから適用する。
        const baseCards = overrideRowCards.get(targetRowId) ?? targetRow.cards;
        let nextCards: DispatchCard[] = baseCards;
        const movedCardIds: string[] = [];
        let currentInsertAt = insertAt;
        for (const lr of legRunsToMove) {
          const before = nextCards.length;
          nextCards = placeLegRunInRow(nextCards, lr, targetRow.rotationNumber, currentInsertAt, deliveryListGrouping, targetRow.truckId);
          // 挿入された分だけ次の挿入位置を進める。同地点 unloading にマージされた場合は
          // 配列長は変わらない (= 次もここに刺す) ので before/after 差分を素直に使えば OK。
          const added = nextCards.length - before;
          currentInsertAt += added;
          // 配置先カード (新規 / リプレイス / マージ いずれも) を legRunIds で同定して id 収集
          const placed = nextCards.find(
            (c) => c.type === 'delivery' && lr.id != null && (c.legRunIds ?? []).includes(lr.id),
          );
          if (placed) movedCardIds.push(placed.id);
        }

        mutateRowCards(targetRowId, () => nextCards);
        const moveIdSet = new Set(idsToMove);
        setOverrideUnassigned((prev) => (prev ?? serverUnassigned).filter((u) => !moveIdSet.has(u.id)));
        if (isMulti) setSelectedUnassignedIds(new Set());
        boardStore.flashRecentlyMoved(movedCardIds);
        return;
      }

      // 2) 車両row → 未割当 (マージ済み legRun は個別の未割当アイテムに展開)
      const sourceLoc = cardLookup.get(activeId);
      if (!sourceLoc) return;
      const sourceRow = rowLookup.get(sourceLoc.rowId);
      const sourceCard = sourceRow?.cards[sourceLoc.index];
      if (!sourceRow || !sourceCard || sourceCard.type !== 'delivery') return;
      // 固定ルート由来の土台カード (fr-template) は本物の案件ではないため移動禁止。
      // DispatchCardView 側で disabled にしているが、念のためここでも弾く (元位置に戻る)。
      if (sourceCard.status === 'fr-template') return;

      if (overId === 'unassigned') {
        const legRunIdsInCard = sourceCard.legRunIds ?? [];
        const newItems: UnassignedItem[] = [];
        if (legRunIdsInCard.length > 0) {
          legRunIdsInCard.forEach((lrId, i) => {
            const lr = legRuns.find((r) => r.id === lrId);
            if (lr) newItems.push(toUnassignedItem(lr, i));
          });
        } else {
          newItems.push({
            id: sourceCard.id,
            storeName: sourceCard.storeName,
            storeAddress: sourceCard.storeAddress,
            cases: sourceCard.todayCases ?? sourceCard.cases,
            origin: sourceCard.origin,
            destination: sourceCard.destination,
            originTimeWindow: sourceCard.originTimeWindow,
            destTimeWindow: sourceCard.destTimeWindow,
            shippers: sourceCard.shippers,
          });
        }
        mutateRowCards(sourceRow.rowId, (cards) => cards.filter((c) => c.id !== activeId));
        setOverrideUnassigned((prev) => [...newItems, ...(prev ?? serverUnassigned)]);
        return;
      }

      // 3) 車両row → 車両row
      // loading / no-delivery カード上にドロップした場合 overId は `${id}-nodrag` なので剥がす
      const lookupId = overId.endsWith('-nodrag') ? overId.slice(0, -'-nodrag'.length) : overId;
      const targetRowId = overId.startsWith('vehicle-row-') ? overId : cardLookup.get(lookupId)?.rowId;
      if (!targetRowId) return;
      const targetRow = rowLookup.get(targetRowId);
      if (!targetRow) return;

      const overLoc = cardLookup.get(lookupId);
      if (sourceRow.rowId === targetRow.rowId) {
        // 同row並び替え
        const insertAt = overLoc ? overLoc.index : targetRow.cards.length - 1;
        if (insertAt === sourceLoc.index) return;
        mutateRowCards(sourceRow.rowId, (cards) => {
          const next = [...cards];
          const [moved] = next.splice(sourceLoc.index, 1);
          next.splice(Math.min(insertAt, next.length), 0, moved);
          return next;
        });
        boardStore.flashRecentlyMoved([activeId]);
        return;
      }

      // 別row: source から除去し、target に legRun 単位で merge 配置
      const legRunIdsInCard = sourceCard.legRunIds ?? [];
      const insertAtBase = overLoc ? overLoc.index : targetRow.cards.length;
      mutateRowCards(sourceRow.rowId, (cards) => cards.filter((c) => c.id !== activeId));

      // 配置結果は state setter の外で確定させてから適用する (setState updater 内の
      // 副作用収集は strict mode の二重呼び出しで取り逃しになることがあるため)
      const targetBase = overrideRowCards.get(targetRow.rowId) ?? targetRow.cards;
      const movedCardIds: string[] = [];
      let nextTargetCards: DispatchCard[];
      if (legRunIdsInCard.length > 0) {
        let targetCards: DispatchCard[] = targetBase;
        for (const lrId of legRunIdsInCard) {
          const lr = legRuns.find((r) => r.id === lrId);
          if (lr) targetCards = placeLegRunInRow(targetCards, lr, targetRow.rotationNumber, insertAtBase, deliveryListGrouping, targetRow.truckId);
          const placed = targetCards.find(
            (c) => c.type === 'delivery' && (c.legRunIds ?? []).includes(lrId),
          );
          if (placed) movedCardIds.push(placed.id);
        }
        nextTargetCards = targetCards;
      } else {
        // legRunId 情報がない場合は従来どおりカードをそのまま挿入
        const next = [...targetBase];
        next.splice(insertAtBase, 0, sourceCard);
        nextTargetCards = next;
        movedCardIds.push(sourceCard.id);
      }
      mutateRowCards(targetRow.rowId, () => nextTargetCards);
      boardStore.flashRecentlyMoved(movedCardIds);
    }, [
      cardLookup,
      rowLookup,
      mutateRowCards,
      unassigned,
      serverUnassigned,
      legRuns,
      selectedUnassignedIds,
      boardStore,
      overrideRowCards,
      deliveryListGrouping,
    ]);

    const onToggleRightPanel = useCallback(() => setIsRightPanelOpen((prev) => !prev), []);

    // ─────────────────────────────────────────────────────────────
    // 行 (= truck × rotation) → 代表 delivery の shift_truck_id を解決する。
    // 仮想回転 (extraRotations 由来 / operations 未作成の 2 回転目以降) は
    // rowIdToShiftTruckId に登録されていないので、rowId から truckId を取り出して
    // 同 truck の代表 delivery (truckIdToDeliveryId) にフォールバックする。
    // V3Plans CyclePresenter では「cycle 選択 = その delivery が選択された」と扱うのと同等の挙動。
    const resolveShiftTruckIdFromRowId = useCallback((rowId: string): number | null => {
      const direct = rowIdToShiftTruckId.get(rowId);
      if (direct != null) return direct;
      const m = /^vehicle-row-(\d+)-/.exec(rowId);
      if (!m) return null;
      const truckId = parseInt(m[1], 10);
      return truckIdToDeliveryId.get(truckId) ?? null;
    }, [rowIdToShiftTruckId, truckIdToDeliveryId]);

    // 印刷 / 帳票出力 (V3Plans と共通の useReportPrinting hook を利用)
    // ─────────────────────────────────────────────────────────────
    const printTargetShiftIds = useMemo(() => {
      const allShiftIds = deliveries.map((d) => d.shiftId).filter((id): id is number => typeof id === 'number');
      const selectedShiftIds = new Set<number>();
      selectedRowIds.forEach((rowId) => {
        const stid = resolveShiftTruckIdFromRowId(rowId);
        if (stid == null) return;
        const d = deliveries.find((x) => x.id === stid);
        if (d?.shiftId != null) selectedShiftIds.add(d.shiftId);
      });
      return selectedShiftIds.size > 0 ? Array.from(selectedShiftIds) : allShiftIds;
    }, [deliveries, selectedRowIds, resolveShiftTruckIdFromRowId]);
    const reportPrinting = useReportPrinting({
      startOn,
      endOn,
      shiftIds: printTargetShiftIds,
      onAnalyticsEvent: (action, buttonName) =>
        ReactGA.event(action, { screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES, button_name: buttonName }),
    });

    // ─────────────────────────────────────────────────────────────
    // 自動配車ボタン: V3Plans / V3Timeline と共通の PlanningPresenter ダイアログを使用する。
    // 「実行する」で V5 normalPlanning mutation を呼び、通常の自動配車 API を走らせる。
    // ─────────────────────────────────────────────────────────────
    const [planningDialogOpen, setPlanningDialogOpen] = useState(false);
    // 移動モード: 詳細ポップの「他車両へ配車」経由でダイアログを開いたときに target 情報を保持する。
    // セット済みのときは planningRequest で transferPlanning を走らせる。
    const [moveTarget, setMoveTarget] = useState<MoveTarget | null>(null);
    const relaxedRules = useMemo(() => RelaxedRuleValues.map((it) => it), []);
    const [selectedRelaxedRules, setSelectedRelaxedRules] = useState<RelaxedRuleVo[]>([]);
    const [priorityLargeTrucks, setPriorityLargeTrucks] = useState<number>(0);

    // PlanningPresenter の設定 state・updater は共有 hook に集約する（Task 6-a）。
    // /map と共有するのはここまでで、selectedRelaxedRules / priorityLargeTrucks は
    // 従来どおり hook の外に残す（PlanningPresenter が setSelectedRelaxedRules を
    // 直接受け取る形のため。usePlanningDialogSettings.ts 冒頭のコメント参照）。
    const { settings: planningDialogSettings, presenterProps: planningDialogSettingProps } = usePlanningDialogSettings();

    /**
     * 自動配車の可否。/map と同じく「ライセンス・実行中」と「選択」の 2 層で決める
     * （混ぜるとどちらを直せば押せるのか利用者に伝わらない）。
     */
    const autoDispatchAvailability = useMemo(
      () => resolveAutoDispatchAvailability({
        isLoading: deliveriesQuery.isLoading,
        useAlgorithmPlanning,
        selectedCompanyId,
        isBusy: controller.isBusy,
        isResetting: operationBulkResetting.isPending,
        kind: 'normal',
      }),
      [deliveriesQuery.isLoading, useAlgorithmPlanning, selectedCompanyId,
        controller.isBusy, operationBulkResetting.isPending],
    );

    /**
     * 選択の層。判定は /map と同じ関数を使う。
     *
     * ただし「計画あり」の見方だけカード配車の事情を足す。この画面は D&D で置いた
     * カードを自動配車で保存する流れがあり、置いた直後のカードは status が template
     * （未反映）なので、サーバの配車だけを見ると「割り当てるものが無い」と誤判定して
     * 保存できなくなる。/map は D&D が無いので、この差で挙動は変わらない。
     */
    const autoDispatchSelectionReason = useMemo(() => {
      const hasSelectedVehicle = rowGroups.some(
        (g) => g.rows.some((r) => selectedRowIds.has(r.rowId)),
      );
      return resolveAutoDispatchSelectionReason({
        hasSelectedVehicle,
        allSelectedVehiclesHaveNoPlan: allSelectedShiftsHaveNoPlan(
          selectedRowIds,
          rowGroups,
          hasDispatchableCard,
        ),
        hasSelectedUnassigned: selectedUnassignedIds.size > 0,
      });
    }, [rowGroups, selectedRowIds, selectedUnassignedIds]);

    // ライセンス等の理由を優先する。選択を直しても押せないものを先に伝える
    const autoDispatchDisabledReason = useMemo<string | null>(
      () => (autoDispatchAvailability.disabled
        ? autoDispatchAvailability.tooltip
        : autoDispatchSelectionReason),
      [autoDispatchAvailability, autoDispatchSelectionReason],
    );

    const handleAutoDispatchClick = useCallback(() => {
      if (autoDispatchDisabledReason !== null) {
        enqueueSnackbar(autoDispatchDisabledReason);
        return;
      }
      setPlanningDialogOpen(true);
    }, [autoDispatchDisabledReason, enqueueSnackbar]);

    const handlePlanningDialogClose = useCallback(() => {
      setPlanningDialogOpen(false);
      setMoveTarget(null);
    }, []);

    // PlanningPresenter は shift_id を要求する（convertToRequestBody → shift_ids に渡る）
    // 選択行 → delivery.id → delivery.shiftId に変換。
    // 仮想回転 (operations 未作成の 2 回転目以降) は rowIdToShiftTruckId に直接無いので
    // resolveShiftTruckIdFromRowId で同 truck の代表 delivery にフォールバックする。
    // 移動モードでは moveTarget から導出した値を優先する。
    const selectedDeliveryIdsForDialog = useMemo(() => {
      if (moveTarget) return [moveTarget.targetShiftId];
      const idSet = new Set<number>();
      selectedRowIds.forEach((rowId) => {
        const stid = resolveShiftTruckIdFromRowId(rowId);
        if (stid == null) return;
        const delivery = deliveries.find((d) => d.id === stid);
        if (delivery?.shiftId != null) idSet.add(delivery.shiftId);
      });
      return Array.from(idSet);
    }, [moveTarget, selectedRowIds, resolveShiftTruckIdFromRowId, deliveries]);

    const selectedOrderIdsForDialog = useMemo(() => {
      if (moveTarget) return moveTarget.orderIds;
      return parseUnassignedIds(selectedUnassignedIds);
    }, [moveTarget, selectedUnassignedIds]);

    // PlanningPresenter が「実行する」を押したときに呼ぶ関数。
    // 通常モードは normalPlanning、移動モード (moveTarget セット) は transferPlanning を使う。
    const planningRequest = useCallback((shiftIds: number[], orderIds: number[]) => {
      if (shiftIds.length === 0) {
        enqueueSnackbar('配車する車両を選択してください');
        return;
      }
      const planning = buildPlanningEntity(planningDialogSettings, {
        startOn, endOn, selectedRelaxedRules, priorityLargeTrucks,
      });

      // 通知・onStarted の発火・完了時の後始末は controller の 8 hooks
      // （autoDispatchHooks）が持つ。ここは planning 本体の組み立てとモード分岐だけ行う。
      if (moveTarget) {
        // 移動モード: V3Plans の transferRequest と同じく、対象 legRun の reset →
        // transferPlanning は controller.runTransfer が持つ
        void controller.runTransfer({
          orderIds: moveTarget.orderIds,
          transferToShiftId: moveTarget.targetShiftId,
          transferFromShiftId: moveTarget.sourceShiftId,
          planning,
        });
        setMoveTarget(null);
        return;
      }

      void controller.runNormal({
        shiftIds,
        orderIds,
        planning,
        requestAsync: true,
        selectedCycleIndexes: [],
        deliveryOperations: [],
        planningSetting: {
          deliveryConditions: {},
          orderConditions: {},
          planningConditions: {},
        } as unknown as PlanningSettingRequest,
      });
    }, [
      startOn,
      endOn,
      selectedRelaxedRules,
      priorityLargeTrucks,
      planningDialogSettings,
      controller,
      moveTarget,
      enqueueSnackbar,
    ]);

    // 固定ルート反映中は Backdrop で操作ブロック。
    // depot operation も operation_snapshot から復元するため、apply 後の自動配車チェーンは不要。
    const applyChainBusy = isApplying;
    // ツールバー経由の「呼び出し」と未割当パネルの「対応づけ」で文言を分ける (applyMode)
    const applyChainBusyMessage = applyMode === 'call'
      ? '固定ルート呼び出し中...'
      : '固定ルートに対応づけ中...';

    // ─────────────────────────────────────────────────────────────
    // 詳細パネルのアクション（編集 / 他車両へ移動 / 未割当に戻す / 削除 / 分割）
    // 実体は useDispatchDetailActions（地図配車と共有）。ここでは画面固有の機構だけ注入する。
    // ─────────────────────────────────────────────────────────────
    // 未割当案件の削除 (V3Plans 等で利用されている useMutationShipmentOrder().bulkDelete を再利用)。
    // unassigned id (= `lr-{legRunId}`) → legRun → shipment_order_id にマップ。
    // 同じ shipment_order に複数 legRun があれば 1 つの shipment_order_id にまとまる。
    const { bulkDelete: deleteShipmentOrders, cloneShipmentOrder } = useMutationShipmentOrder();
    // handleDeleteSelectedUnassigned の依存を安定させる。useMutation の戻り値は毎レンダー
    // 新規オブジェクトなので、実際に呼んでいる mutateAsync だけを依存に使う
    // （useShiftDeletion.ts の removeMutateAsync と同じ手口）
    const deleteShipmentOrdersMutateAsync = deleteShipmentOrders.mutateAsync;

    const resolveShipmentOrderIds = useCallback(
      (ids: string[]) => resolveShipmentOrderIdsFromUnassignedIds(ids, legRuns),
      [legRuns],
    );

    // 未割当の楽観削除。機構 (配列 override の上書き・操作の終端で畳む) は
    // useUnassignedRemovalOptimistic に切り出してある
    const unassignedOptimistic = useUnassignedRemovalOptimistic({
      serverItems: serverUnassigned,
      overrideItems: overrideUnassigned,
      setOverrideItems: setOverrideUnassigned,
      selectedIds: selectedUnassignedIds,
      setSelectedIds: setSelectedUnassignedIds,
      scopeKey: planningRangeKey,
    });

    // notify は useRotationEditing / useShiftDeletion への注入（:621 相当）のため前段へ移動済み。
    const refetchLegRuns = useCallback(() => { void legRunsQuery.refetch(); }, [legRunsQuery]);
    const handleCloseSelectedCards = useCallback(() => { boardStore.clearSelectedCards(); }, [boardStore]);
    const openTransferPlanning = useCallback((target: MoveTarget) => {
      setMoveTarget(target);
      setPlanningDialogOpen(true);
    }, []);
    // scope（会社・対象日）が変わったら D&D 楽観的上書きを破棄する。
    // overrideRowCards/overrideUnassigned は shouldResetOverrideAfterServerRefresh により
    // 「既に override が入っている間はサーバ再取得だけでは消さない」設計 (進行中の自動配車の
    // 途中経過を守るため) なので、会社を切り替えても自動では消えない。選択行 (selectedRowIds)
    // は planningRangeKey 依存の別 effect が処理するのでここでは扱わない。
    // クリア本体は resetOverrideForScopeChange（autoDispatchQueue.ts）に抽出済み
    // （autoDispatchQueue.test.ts で単体テスト・破壊確認済み）。
    const handleScopeChanged = useCallback(() => {
      resetOverrideForScopeChange({ setOverrideRowCards, setOverrideUnassigned, hasQueuedOverrideChangeRef });
    }, []);

    const detailActions = useDispatchDetailActions({
      deliveries,
      rowGroups,
      legRuns,
      screenName: SCREEN_NAMES.CONVENIENCE_DISPATCHES,
      scopeKey: planningRangeKey,
      applyResetToCaches,
      operationBulkResetting,
      deleteShipmentOrders,
      cloneShipmentOrder,
      refetchLegRuns,
      setConfirmDialog,
      notify,
      unassignedOptimistic,
      onCloseDetail: handleCloseSelectedCards,
      onScopeChanged: handleScopeChanged,
      openTransferPlanning,
      resetTransferPlanning: handlePlanningDialogClose,
    });
    const {
      detailSelection, detailClickRect, detailOpen, openDetail, closeDetail,
      moveMenuAnchor, setMoveMenuAnchor, duplicateStoreNames, geoAlerts,
      laneSheetId, editShipmentOrderId, splitDialogOpen, splitTargetLegRun,
    } = detailActions;

    // 詳細の開閉で onSelectCard を作り直さないための ref。
    // Why not 依存配列に入れる: カードをクリックすると必ず detailSelection が変わるので、
    // 依存に入れると毎クリックで callback が別物になり、Board の memo が破れて
    // 全行・全カードが描き直される（busyStateRef / detailKeyRef と同じ手口）。
    const detailSelectionRef = useRef(detailSelection);
    useLayoutEffect(() => { detailSelectionRef.current = detailSelection; }, [detailSelection]);

    const handleOpenAddVehicleDialog = useCallback(() => { setAddVehicleDialogOpen(true); }, []);
    const handleCloseAddVehicleDialog = useCallback(() => { setAddVehicleDialogOpen(false); }, []);
    const handleOpenTariffDialog = useCallback(() => { setTariffDialogOpen(true); }, []);

    // 未割当カードの詳細パネルを開く。rect は呼び出し側が計測済みの値を渡す
    const handleShowUnassignedDetail = useCallback(
      (item: UnassignedItem, rect: { top: number; left: number; right: number; height: number }) => {
        openDetail({ source: 'unassigned', item }, rect);
      },
      [openDetail],
    );

    // 固定ルート未選択のときはボタン自体を出さない挙動を変えないため、
    // useCallback した関数を条件で出し入れする（関数の中で早期 return にはしない）
    const handleAlignToFixedRouteWithConfirm = useCallback(() => {
      if (fixedRouteId == null) return;
      const currentRoute = fixedRoutes.find((r) => r.id === fixedRouteId);
      const routeName = currentRoute?.name ?? '選択中の固定ルート';
      setConfirmDialog({
        title: '出荷データを固定ルートに対応づけ',
        description: `「${routeName}」に現在の出荷データ（${unassigned.length}件）を対応づけます。よろしいですか？`,
        onConfirm: () => { onAlignToFixedRoute(fixedRouteId); },
      });
    }, [fixedRouteId, fixedRoutes, unassigned.length, onAlignToFixedRoute]);

    const onSelectCard = useCallback((
      card: DispatchCard,
      vehicle: Vehicle,
      orderIndex: number,
      event: MouseEvent<HTMLDivElement> | KeyboardEvent<HTMLDivElement>,
    ) => {
      // 固定ルートの土台 (fr-template = 未割当 legRun 候補 / no-delivery = 本日なし) は
      // 本物の案件ではないので、クリックしても詳細パネル / 選択ハイライトを開かない。
      // ドラッグ不可と合わせて完全に「触れない」UI に揃える。
      if (
        card.type === 'delivery'
        && (card.status === 'fr-template' || card.status === 'no-delivery')
      ) {
        return;
      }
      const current = detailSelectionRef.current;
      const { nextSelectedCardIds, detail } = resolveCardClick(
        card.id,
        boardStore.getState().selectedCardIds,
        current?.source === 'vehicle' ? current.card.id : null,
      );
      boardStore.setSelectedCardIds(nextSelectedCardIds);
      if (detail === 'keep') return;
      if (detail === 'close') {
        openDetail(null, null);
        return;
      }
      const target = event.currentTarget as HTMLElement | null;
      const rect = target?.getBoundingClientRect() ?? null;
      openDetail(
        { source: 'vehicle', card, vehicle, orderIndex },
        rect ? { top: rect.top, left: rect.left, right: rect.right, height: rect.height } : null,
      );
    }, [boardStore, openDetail]);

    const handleDeleteSelectedUnassigned = useCallback(() => {
      const ids = Array.from(selectedUnassignedIds);
      if (ids.length === 0) return;
      const shipmentOrderIds = resolveShipmentOrderIds(ids);
      if (shipmentOrderIds.length === 0) {
        enqueueSnackbar('削除対象の案件が見つかりません', { variant: 'error' });
        return;
      }
      // 削除対象 shipment_order 配下の legRun id 全てを unassigned から取り除く必要があるため
      // (1 shipment_order が複数 legRun を持つ場合に備える)
      const shipmentOrderIdSet = new Set(shipmentOrderIds);
      const removedItemIds = new Set<string>();
      legRuns.forEach((lr) => {
        if (lr.id != null && shipmentOrderIdSet.has(lr.shipment_order_id)) {
          removedItemIds.add(`lr-${lr.id}`);
        }
      });
      setConfirmDialog({
        title: '案件を削除しますか？',
        description: `${shipmentOrderIds.length} 件の案件を削除します。元には戻せません。`,
        onConfirm: async () => {
          // 楽観的更新: API レスポンス前に画面から削除対象を消す。
          const removal = unassignedOptimistic.remove(removedItemIds);

          try {
            await deleteShipmentOrdersMutateAsync(shipmentOrderIds);
            enqueueSnackbar(`${shipmentOrderIds.length} 件の案件を削除しました`, { variant: 'success' });
            // 成功時も操作を終端する。override は serverUnassigned が削除を反映した時点で
            // 畳まれる (useUnassignedRemovalOptimistic)。
            removal.commit();
          } catch (e) {
            const err = e as AxiosError<{ message: string }>;
            enqueueSnackbar(err.response?.data?.message ?? '削除に失敗しました', { variant: 'error' });
            // 失敗時はこの操作で消した分だけ戻す
            removal.rollback();
          }
        },
      });
    }, [
      selectedUnassignedIds, resolveShipmentOrderIds,
      deleteShipmentOrdersMutateAsync, enqueueSnackbar, legRuns, unassignedOptimistic,
    ]);

    return (
      // DndContext と DragOverlay の両方がこの内側に居る位置に置く（DragOverlay の
      // 中身 (DragOverlayCard) が store を購読するため）。
      <BoardInteractionProvider store={boardStore}>
        <title>配車計画 | カード</title>
        <ConvenienceDispatchAddVehicleDialog
          open={addVehicleDialogOpen}
          onClose={handleCloseAddVehicleDialog}
          startOn={startOn}
          endOn={endOn}
          existingTruckIds={existingTruckIds}
          existingDriverIds={existingDriverIds}
          trucks={trucks}
          drivers={drivers}
        />
        <NotAllocSuggestionComponent
          showNotAllocSuggestionSnackbar={showNotAllocSuggestionSnackbar}
          suggestionDialogOpen={suggestionDialogOpen}
          notAllocSuggestions={notAllocSuggestions}
          handleSuggestionDialogClose={handleSuggestionDialogClose}
          setShowNotAllocSuggestionSnackbar={setShowNotAllocSuggestionSnackbar}
          setSuggestionDialogOpen={setSuggestionDialogOpen}
          // ConvenienceDispatches は V3Plans の actionBar が無いので actionBarHeight 分上に上げる
          snackbarTopOffsetPx={appBarHeight - 15}
        />
        <DispatchDetailDialogs
          laneSheetId={laneSheetId}
          onCloseLaneSheet={detailActions.closeLaneSheet}
          editShipmentOrderId={editShipmentOrderId}
          onCloseEditShipmentOrder={detailActions.closeEditShipmentOrder}
          startOn={startOn}
          splitDialogOpen={splitDialogOpen}
          splitTargetLegRun={splitTargetLegRun}
          onCloseSplitDialog={detailActions.closeSplitDialog}
          onSplitSuccess={detailActions.handleSplitSuccess}
        />
        <Dialog
          open={planningDialogOpen}
          onClose={handlePlanningDialogClose}
          fullWidth
          maxWidth={false}
          PaperProps={{ sx: { maxWidth: 720 } }}
        >
          <PlanningPresenter
            screenName="convenience_dispatch"
            {...planningDialogSettingProps}
            planningRequest={planningRequest}
            onClose={handlePlanningDialogClose}
            selectedDeliveryIds={selectedDeliveryIdsForDialog}
            selectedOrderIds={selectedOrderIdsForDialog}
            startOn={startOn}
            relaxedRules={relaxedRules}
            selectedRelaxedRules={selectedRelaxedRules}
            setSelectedRelaxedRules={setSelectedRelaxedRules}
            setPriorityLargeTrucks={setPriorityLargeTrucks}
            priorityLargeTrucks={priorityLargeTrucks}
            hiddenSelectedOrderCount={hiddenSelectedCount}
          />
        </Dialog>
        <Box sx={{ display: 'flex', flexDirection: 'column', height: `calc(100vh - ${appBarHeight}px)`, overflow: 'hidden' }}>
          <ConvenienceDispatchToolbar
            startOn={startOn}
            endOn={endOn}
            fixedRouteId={fixedRouteId}
            fixedRoutes={fixedRoutes}
            isLoadingFixedRoutes={isLoadingFixedRoutes}
            isApplying={isApplying}
            onDateRangeChange={onDateRangeChange}
            onFixedRouteSelect={onFixedRouteSelect}
            onFixedRouteCreated={onFixedRouteCreated}
            onFixedRouteCleared={onFixedRouteCleared}
            onApplyFixedRoute={onApplyFixedRoute}
            displayMetrics={displayMetrics}
            onDisplayMetricsChange={setDisplayMetrics}
            orderAttrFilter={orderAttrFilter}
            onOrderAttrFilterChange={onOrderAttrFilterChange}
            onNavigate={onNavigate}
            visibleColumns={visibleColumns}
            onVisibleColumnsChange={setVisibleColumns}
            selectorDialogOpen={fixedRouteSelectorOpen}
            onSelectorDialogOpenChange={setFixedRouteSelectorOpen}
            onAutoDispatch={handleAutoDispatchClick}
            // 押せない理由があるならボタンも押せなくする（/map と同じ扱い）。
            // 実行中しか見ていなかったため、車両を選んでいなくても押せる見た目だった
            isAutoDispatchDisabled={autoDispatchAvailability.disabled || autoDispatchSelectionReason !== null}
            autoDispatchTooltip={autoDispatchDisabledReason ?? undefined}
            customInputFieldNames={customInputFieldNames}
            isLicenseReady={isLicenseReady}
            screenName={SCREEN_NAMES.CONVENIENCE_DISPATCHES}
          />

          <DndContext
            sensors={sensors}
            collisionDetection={collisionDetection}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragCancel={handleDragCancel}
            onDragEnd={handleDragEnd}
          >
            <Box
              ref={paneContainerRef}
              data-testid="dispatch-pane-container"
              sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}
            >
              {/* 左+中央ラッパー: 外側Yスクロールで左右を一緒にスクロール */}
              <Box sx={{ flex: 1, overflowY: 'auto' }}>
                <Box sx={{ display: 'flex', minHeight: '100%', minWidth: 'max-content' }}>
                  {/*
                    左カラム: 縦は外側のスクロールに同乗する。横スクロールでは sticky left で
                    残す（流れると車両名が見えなくなる）。中央のレーンより手前に置く。
                  */}
                  <Box
                    data-testid="vehicle-column"
                    sx={{
                      // 既定は 320（車両カードは width: 100% なので、広げたぶんだけ本文が入る）。
                      // 幅はハンドルのドラッグで変えられる（#312）
                      width: fittedLeftPanelWidth,
                      flexShrink: 0,
                      position: 'sticky',
                      left: 0,
                      zIndex: 40,
                      borderRight: 1,
                      borderColor: 'divider',
                      bgcolor: 'background.paper',
                    }}
                  >
                    <ConvenienceDispatchVehicleList
                      canSortTruckDisplayOrder={canSortTruckDisplayOrder}
                      isMutatingTruckOrder={truckDisplayOrderMutation.isPending}
                      onTruckOrderChange={handleTruckOrderChange}
                      rowGroups={visibleRowGroups}
                      isLoading={deliveriesQuery.isLoading}
                      displayMetrics={vehicleDisplayMetricSet}
                      grossProfitByTruckId={grossProfitByTruckId}
                      constraintAlertsByRowId={constraintAlertsByRowId}
                      peakLoadByRowId={peakLoadByRowId}
                      selectedRowIds={selectedRowIds}
                      allSelected={allSelected}
                      truckDisplayStatus={truckDisplayStatus}
                      onTruckDisplayStatusChange={setTruckDisplayStatus}
                      rotations={rotations}
                      onRotationsChange={setRotations}
                      constraintViolationOnly={constraintViolationOnly}
                      onConstraintViolationOnlyChange={setConstraintViolationOnly}
                      hasSelection={selectedRowIds.size > 0}
                      driverNameById={driverNameById}
                      truckGroupEntitiesWithAll={truckGroupEntitiesWithAll}
                      selectedTruckGroupIndices={selectedTruckGroupIndices}
                      onSelectedTruckGroupIndicesChange={setSelectedTruckGroupIndices}
                      onRowToggle={onRowToggle}
                      registerRow={registerRow}
                      onSelectAll={onSelectAll}
                      onDeselectAll={onDeselectAll}
                      onUnassignSelected={handleUnassignSelected}
                      onSwapSelected={handleSwapSelected}
                      onAddVehicle={handleOpenAddVehicleDialog}
                      onDeleteShift={requestDeleteShift}
                      onAddRotation={handleAddRotation}
                      onDeleteRotation={handleDeleteRotation}
                      onTruckEdit={vehicleAssignmentHandlers.onTruckEdit}
                      onDriverEdit={vehicleAssignmentHandlers.onDriverEdit}
                      onPlateNumberEdit={vehicleAssignmentHandlers.onPlateNumberEdit}
                      onOpenPrint={reportPrinting.open}
                      screenName={SCREEN_NAMES.CONVENIENCE_DISPATCHES}
                    />
                  </Box>

                  {/*
                    左カラムのリサイズハンドル。左カラム (sticky left: 0) の右隣へ貼り付ける。

                    Why sticky: このハンドルは横スクロールする器の中にある。sticky にしないと
                    横へスクロールした瞬間にレーンの下へ流れて掴めなくなる（左カラムだけ残って
                    境界に何も無い状態になる）。left は左カラムの幅そのもの。

                    Why not 中央カラム側や器の外に置く: 左カラムは中央と縦スクロールを共有する
                    ため器の中にあり、器の外に出せる境界が無い。

                    zIndex は左カラム (40) と揃える。中央の列番号ヘッダ (sticky top・zIndex 2)
                    より上に置かないと、ヘッダの帯と重なる高さだけ掴めなくなる。
                  */}
                  <PanelResizeHandle
                    side="left"
                    width={fittedLeftPanelWidth}
                    defaultWidth={DEFAULT_LEFT_PANEL_WIDTH}
                    label="左リストの幅を調整"
                    screenName={SCREEN_NAMES.CONVENIENCE_DISPATCHES}
                    onWidthChange={setLeftPanelWidth}
                    containerWidth={paneContainerWidth}
                    reservedWidth={reservedWidthFor(isRightPanelOpen ? fittedRightPanelWidth : 0, isRightPanelOpen)}
                    sx={{
                      position: 'sticky',
                      left: `${fittedLeftPanelWidth}px`,
                      zIndex: 40,
                      bgcolor: 'background.paper',
                    }}
                  />

                  {/*
                    中央カラム: スクロールは持たない。縦横とも外側の 1 つのコンテナに任せる。

                    Why not overflowX: 'auto' + overflowY: 'visible' / 'clip': CSS では片方向だけ
                    スクロールする要素は作れない（一方が auto なら visible は auto、clip は hidden に
                    化ける）。どちらでもこのカラムが sticky のスクロール祖先になり、縦スクロールで
                    配送順ヘッダが流れて消えていた。ヘッダは sticky top、車両リストは sticky left で
                    残す（表計算のペイン固定と同じ形）。
                  */}
                  <Box data-testid="board-column" sx={{ minWidth: 'max-content', flexShrink: 0 }}>
                    <ConvenienceDispatchBoard
                      rowGroups={visibleRowGroups}
                      isLoading={deliveriesQuery.isLoading}
                      visibleColumns={visibleColumns}
                      displayMetrics={displayMetricSet}
                      duplicateStoreNames={duplicateStoreNames}
                      routeDeviationCardIds={routeDeviationCardIds}
                      geoAlerts={geoAlerts}
                      hasFixedRoutes={fixedRoutes.length > 0}
                      hasAnyDelivery={rowGroups.length > 0}
                      onSelectCard={onSelectCard}
                    />
                  </Box>
                </Box>
              </Box>

              {/* 畳んでいる間は出さない。幅 0 のパネルを掴めてしまう */}
              {isRightPanelOpen && (
                <PanelResizeHandle
                  side="right"
                  width={fittedRightPanelWidth}
                  defaultWidth={DEFAULT_RIGHT_PANEL_WIDTH}
                  label="右リストの幅を調整"
                  screenName={SCREEN_NAMES.CONVENIENCE_DISPATCHES}
                  onWidthChange={setRightPanelWidth}
                  onResizingChange={setRightPanelResizing}
                  containerWidth={paneContainerWidth}
                  reservedWidth={reservedWidthFor(fittedLeftPanelWidth)}
                />
              )}

              {/* 右カラム: 左上に張り出すトグルボタン付き */}
              <Box sx={{ position: 'relative', display: 'flex', height: '100%', minHeight: 0, flexShrink: 0 }}>
                {/* Toggle button: 右パネルの左端に半分はみ出す円ボタン */}
                <Box
                  component="button"
                  type="button"
                  onClick={onToggleRightPanel}
                  aria-label={isRightPanelOpen ? '右パネルを閉じる' : '右パネルを開く'}
                  sx={{
                    position: 'absolute',
                    left: -12,
                    top: 12,
                    zIndex: 40,
                    display: 'flex',
                    height: 24,
                    width: 24,
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '9999px',
                    border: '1px solid hsl(var(--border))',
                    bgcolor: 'hsl(var(--background))',
                    boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
                    cursor: 'pointer',
                    transition: 'background-color 0.15s',
                    '&:hover': { bgcolor: 'hsl(var(--accent))' },
                  }}
                >
                  <Box
                    component="svg"
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    sx={{
                      color: 'hsl(var(--muted-foreground))',
                      transition: 'transform 0.2s',
                      transform: isRightPanelOpen ? 'rotate(0deg)' : 'rotate(180deg)',
                    }}
                  >
                    <polyline points="9 18 15 12 9 6" />
                  </Box>
                </Box>

                <Box
                  data-testid="unassigned-column"
                  sx={{
                    height: '100%',
                    minHeight: 0,
                    borderLeft: 1,
                    borderColor: 'divider',
                    // 開閉アニメーションはドラッグ中だけ切る。効いたままだと指の動きに
                    // 0.2s 遅れて追いかけてくる
                    transition: rightPanelResizing ? 'none' : 'width 0.2s',
                    overflow: 'hidden',
                    // 既定は 380。幅はハンドルのドラッグで変えられる（#312）
                    width: isRightPanelOpen ? fittedRightPanelWidth : 0,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <ConvenienceDispatchUnassignedList
                    screenName={SCREEN_NAMES.CONVENIENCE_DISPATCHES}
                    // 地点単位トグルはこの画面に無いので現状は効かないが、渡さないでおくと
                    // 後でトグルを出したときだけ設定が抜ける
                    deliveryListGrouping={deliveryListGrouping}
                    items={unassigned}
                    rowGroups={rowGroups}
                    onSelectVehicleRows={onSelectVehicleRows}
                    // ツールチップの乗務者名は行の driverId から引く (log_web_issues#323)
                    driverNameById={driverNameById}
                    orderAttrFilter={orderAttrFilter}
                    deliveries={enrichedDeliveries}
                    isLoading={legRunsQuery.isLoading}
                    displayMetrics={displayMetricSet}
                    selectedItemIds={selectedUnassignedIds}
                    onSelectItem={onSelectUnassigned}
                    onHiddenSelectedCountChange={setHiddenSelectedCount}
                    onShowDetail={handleShowUnassignedDetail}
                    notAllocReasonsByOrderId={notAllocReasonsByOrderId}
                    notAllocDeliveries={notAllocDeliveriesForReason}
                    notAllocTrucks={trucks}
                    notAllocDrivers={drivers}
                    // 固定ルートが選択されているときだけ「固定ルートに対応づけ」ボタンを有効化する
                    // クリック時は確認ダイアログを挟んでから実行する（サンプル準拠）
                    onAlignToFixedRoute={fixedRouteId != null ? handleAlignToFixedRouteWithConfirm : undefined}
                    isAligningToFixedRoute={isApplying}
                    onDeleteSelected={handleDeleteSelectedUnassigned}
                    onRestoreSplit={handleRestoreSplit}
                    customInputFieldNames={customInputFieldNames}
                    trucksForFilter={trucksForFilter}
                    driversForFilter={driversForFilter}
                    companyId={companyId}
                    isLicenseReady={isLicenseReady}
                    isMastersReady={isMastersReady}
                    isMastersError={isMastersError}
                    onOpenTariffCalculation={handleOpenTariffDialog}
                    tariffCalculationDisabledReason={tariffDisabledReason ?? undefined}
                  />
                </Box>
              </Box>
            </Box>

            <DragOverlay>
              <DragOverlayCard displayMetrics={displayMetricSet} />
            </DragOverlay>
          </DndContext>

          {/* カード詳細ポップオーバー (route-planner の DispatchDetailPanel) */}
          {detailOpen && detailSelection && (() => {
            const selectedDeliveryCard =
              detailSelection.source === 'vehicle' && detailSelection.card.type === 'delivery'
                ? detailSelection.card
                : null;
            const hasDuplicate = selectedDeliveryCard
              ? duplicateStoreNames.has(selectedDeliveryCard.storeName)
              : false;
            const selectedId =
              detailSelection.source === 'vehicle'
                ? detailSelection.card.id
                : detailSelection.item.id;
            const geoAlert = geoAlerts.get(selectedId);
            // 選択中カードが載っている row は移動先から外す。積地カードも移動元になる
            // (logposeTech/log_web_issues#320)
            const currentRowId = resolveMoveSourceRowId(detailSelection, rowGroups);
            // 除外・重複排除は buildMoveTargets に閉じ込める（rowId ではなく shiftId で判定する）
            const shiftIdOfRow = (rowId: string) => {
              const delivery = deliveries.find((d) => d.id === (rowIdToShiftTruckId.get(rowId) ?? -1));
              return delivery?.shiftId ?? null;
            };
            const moveTargets = buildMoveTargets(
              buildMoveTargetRows(rowGroups, { shiftIdOfRow, driverNameById }),
              { currentRowId, sourceShiftId: currentRowId ? shiftIdOfRow(currentRowId) : null },
            );
            return (
              <DetailPanelShell
                selection={detailSelection}
                placement="anchor"
                clickRect={detailClickRect}
                screenName={detailActions.screenName}
                hasDuplicate={hasDuplicate}
                geoAlert={geoAlert}
                onGeoUpdate={detailActions.handleGeoUpdate}
                moveMenuAnchor={moveMenuAnchor}
                onChangeMoveMenuAnchor={setMoveMenuAnchor}
                onClose={closeDetail}
                onEditOrder={detailActions.handleEditUnassignedOrder}
                onEditLane={detailActions.handleEditLane}
                onMoveToVehicle={detailActions.handleMoveToVehicle}
                onResetToUnassigned={detailActions.handleResetToUnassigned}
                onSplit={detailActions.handleOpenSplitDialog}
                onDelete={detailActions.handleDeleteUnassignedFromDetail}
            onCopy={detailActions.handleCopyUnassignedFromDetail}
                moveTargets={moveTargets}
                // 未割当案件は /plan/ と同じく「案件編集」ダイアログを開く
                resolveShipmentOrderId={detailActions.resolveShipmentOrderId}
              />
            );
          })()}

          {/* 共通確認ダイアログ */}
          <ConfirmDialog
            open={confirmDialog !== null}
            title={confirmDialog?.title}
            message={confirmDialog?.description ?? ''}
            handleCancel={() => setConfirmDialog(null)}
            handleOk={() => {
              const cb = confirmDialog?.onConfirm;
              setConfirmDialog(null);
              if (cb) void cb();
            }}
          />

          {/* 帳票出力 (V3Plans と共通) */}
          <ReportPrintingDialogs {...reportPrinting.dialogProps} />

          <TariffCalculationDialog
            dialogIsOpen={tariffDialogOpen}
            setDialogIsOpen={setTariffDialogOpen}
            selectedDeliveryIds={tariffTargetIds}
            targetDescription={<>対象期間の配車済みルート<strong>全{tariffTargetIds.length}件</strong>について、運賃を再計算して保存します。</>}
            // 進行と結果は右下のトースト（自動配車と同じ場所）で知らせるので、完了を待たずに閉じる
            onExecute={(params) => { setTariffDialogOpen(false); tariffAction.execute(params); }}
            isExecuting={tariffAction.isExecuting}
          />

          {/* 車両 / ドライバー / 車番 編集メニュー (左リストから開く)。/map と同一実体 */}
          <VehicleAssignmentMenus {...vehicleAssignmentMenuProps} />
        </Box>

        {/* 固定ルート適用 → 自動配車の連続フロー中は画面操作をブロック */}
        <Backdrop
          open={applyChainBusy}
          sx={{
            zIndex: (theme) => theme.zIndex.drawer + 1,
            color: '#fff',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <CircularProgress color="inherit" />
          <Typography variant="body1">{applyChainBusyMessage}</Typography>
        </Backdrop>

        {/* 自動配車 / 未割当に戻す 実行中は右下にトーストでフィードバック。
            操作直後 (大量オーダー時) に挙動が無く見えないよう、進行中であることを伝える。
            applyChainBusy で Backdrop が出ている時は重複表示を避けるため非表示。 */}
        {!applyChainBusy
          && (isAutoDispatching || operationBulkResetting.isPending || tariffAction.isExecuting
            || isApplyingVehicleChange) && (
          <Box
            role="status"
            aria-live="polite"
            sx={{
              position: 'fixed',
              bottom: 24,
              right: 24,
              zIndex: (theme) => theme.zIndex.snackbar + 10,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: 2,
              py: 1.25,
              borderRadius: 2,
              bgcolor: 'background.paper',
              color: 'text.primary',
              border: 1,
              borderColor: 'divider',
              boxShadow: '0 10px 30px -10px rgba(0,0,0,0.4)',
              minWidth: 240,
              animation: 'progressToastSlideUp 0.2s ease-out',
              '@keyframes progressToastSlideUp': {
                from: { opacity: 0, transform: 'translateY(8px)' },
                to: { opacity: 1, transform: 'translateY(0)' },
              },
            }}
          >
            <CircularProgress size={20} thickness={4} color="primary" />
            <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {resolveProgressToastLabel({
                  isResetting: operationBulkResetting.isPending,
                  isAutoDispatching,
                  isApplyingVehicleChange,
                  isCalculatingTariff: tariffAction.isExecuting,
                })}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                しばらくお待ちください
              </Typography>
            </Box>
            {/* /map (Map/Presenter.tsx) と同じ停止導線。
                Why not controller.isAccepted を待つ: cancel は POST 応答前でも安全に
                動く（planning がまだ無ければ DELETE を撃たず、running キャッシュの
                invalidate だけで終わる）。待たせると「押しても止まらない時間帯」が
                できるので、実行中はいつでも押せるようにする。
                未割当に戻す最中は出さない（API が 1 本で途中で止められない） */}
            {controller.isBusy && !operationBulkResetting.isPending && (
              <Tooltip title="自動配車の実行を停止する" arrow>
                <IconButton
                  size="small"
                  aria-label="自動配車の実行を停止する"
                  onClick={() => {
                    ReactGA.event('click', {
                      screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES,
                      button_name: '自動配車 停止',
                    });
                    // 文言は V3Plans / /map と同一にする
                    setConfirmDialog({
                      title: '自動配車を停止しますか？',
                      description: '実行中の自動配車を停止します。よろしいですか？',
                      // ユーザーが押した停止。止められなかったときは黙って捨てず伝える
                      onConfirm: () => { controller.cancel({ notifyOnFailure: true }); },
                    });
                  }}
                  sx={{ ml: 'auto', alignSelf: 'flex-start', mt: -0.5, mr: -0.5 }}
                >
                  <CloseRounded sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )}
      </BoardInteractionProvider>
    );
  },
);

export default ConvenienceDispatchesPresenter;
